// Auckland, "Sky Tower over the Viaduct": a mild, clear November night on Karanga Plaza in Wynyard Quarter, at the
// Viaduct Events Centre's south-east corner (36.8408 S, 174.7597 E), looking south-south-east (about 152 deg) across
// the channel to Wynyard Crossing (Te Wero bridge, 80-140 m off), the Viaduct Basin's moored yachts, the Viaduct
// Harbour apartments, the city centre and the Sky Tower (162 deg, 0.9 km). Left to right, as seen from there: the
// city's towers behind the Eastern Viaduct and Te Wero Island (Commercial Bay's PwC Tower, 116 deg, its curved fin of
// a crown just over the Sky Tower's pod; the gold AMP Centre in front of it; the stepped towers of Shortland Street),
// the ANZ Centre's round shaft (150 deg), a rooftop billboard on Customs Street, the Sky Tower standing in the bridge's
// channel between the two bascule leaves and their white A-frame masts, the Viaduct Harbour apartments and their bars
// along the far quay, and Karanga Plaza itself: the tidal steps down to the water, a giant timber lounger on its track,
// harakeke and native grasses, cable-drum tables, and the near corner, a stacked shipping-container pavilion with a bar
// in its lower box, a lounge above and a roof deck, under a pohutukawa in flower. The Harbour Bridge stands behind the
// viewer (north-west) and is not in the frame; the Park Hyatt is behind the pavilion. The 90 deg from the Eastern
// Viaduct to the plaza is compressed into the frame without changing the order.
// The Sky Tower is drawn to its real stages, measured on photos from Wynyard Quarter and Victoria Park: 328 m, the
// 92.6 m mast is the top 0.28; the SkyWalk ring (the pod's widest, 192 m) sits at 0.585 of the height; the ring is
// 2.5 times the shaft's width and 2.9 to 3.1 times shorter than the tip's height above it. From the shaft up: the
// lower pod (1.4 times the shaft) with its dark band and observation windows, a lit lip, the restaurant's dark glazed
// band, the SkyWalk ring with its glass rail, the pod's roof, a silver cone, the Sky Deck's glass ring (220 m), the
// pedestal cone and the mast with its red aircraft lamps. Night: a cool white with a lavender wash; colour shows are
// timed events.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };

/* ================= Layout ================= */
const FAR_WL = 158;                // the Viaduct Basin's far quay: the apartments' foot and the far waterline
const EDGE = 196;                  // top of Karanga Plaza's timber edge: the channel shows from the bridge down to it
const FEET = 207;                  // the plaza's front row of people stands on this row (above the bottom 63 rows)
const MOON = { x: 204, y: 33, r: 6 };
// The Sky Tower: its axis and the rows of its stages (see the header for the ratios they come from)
const ST = { cx: 296, tip: 8, mast: 54, sky: 59, ring: 73, pod1: 95, foot: 170, seen: 141 };
// Wynyard Crossing: the deck runs from Te Wero Island (left, farther) to Karanga Plaza (right, nearer), so it drops a
// little to the right; the two leaves pivot on the piers at PE and PW and meet over the channel's middle (ST.cx)
const BR = { x0: 132, x1: 480, PE: 248, PW: 344 };
const deckY = x => Math.round(167 + (x - BR.x0) * 0.021);
const BB = { x: 186, y: 102, w: 48, h: 20 };      // the rooftop billboard's face on an office block on Customs Street
const PAV = { x0: 362, x1: 474, roof: 110, mid: 158, floor: 207 };   // the stacked container pavilion bar (the near corner)
const LAMP = { x: 100, y: 146 };                   // a plaza light pole (left, near the viewer)

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    if (a >= 255) { d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = 255; return; }
    const k = a / 255;
    d[o] = Math.round(lerp(d[o], rgb[0], k)); d[o + 1] = Math.round(lerp(d[o + 1], rgb[1], k)); d[o + 2] = Math.round(lerp(d[o + 2], rgb[2], k));
    d[o + 3] = Math.max(d[o + 3], a);
  };
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  const line = (x0, y0, x1, y1, col, a = 255) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let k = 0; k <= n; k++) put(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col, a);
  };
  return { c, x, put, fill, line, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], clamp(t, 0, 1))).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
// pick from a dark-to-light ramp with ordered dither
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
const cachedRgba = {};
const rgbaOf = (col, a) => cachedRgba[col + a] || (cachedRgba[col + a] = (([r, g, b]) => `rgba(${r},${g},${b},${a})`)(hex(col)));

/* ================= Sign font: FONT3 with wide M, W, N, K, V (FONT3's read as H and Pi at 1x) ================= */
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep indigo, the city's violet glow low over the towers ================= */
const SKY = ['#03051a', '#050820', '#070b27', '#0a0f2f', '#0d1337', '#11173f', '#151b47', '#1a1f4f', '#202456',
  '#27295d', '#2f2e63', '#383367', '#423869', '#4d3d6a', '#59436a'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 165), 1.6) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - 150) / 260, (y - 160) / 70)) * 0.2;         // the city centre's lights on the haze
    v += Math.max(0, 1 - Math.hypot((i - ST.cx) / 70, (y - 90) / 110)) * 0.08;       // the tower's own wash
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 80, (y - MOON.y) / 50)) * 0.1;    // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.6], [1, 2, 1.4], [2, -2, 1], [-1, 3, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fbfaff' : '#e8e6f4';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#b4b2cc' : '#d8d6ea';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#a8a6c0' : '#c8c6dc';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(64);
  while (STARS.length < 70) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 96);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16 || Math.abs(x - ST.cx) < 14) continue;
    STARS.push({ x, y, k: 10 + Math.floor(r() * 50), ph: r(), b: y < 50 ? 0.45 + r() * 0.5 : 0.2 + r() * 0.3, big: r() < 0.07 });
  }
}
// thin wisps of sea cloud, dithered into the sky with no outline; tileable so they drift
const WISPS = [{ seed: 9, y: 14, h: 12, n: 2, k: 1 }, { seed: 23, y: 40, h: 12, n: 1, k: 2 }, { seed: 31, y: 66, h: 12, n: 2, k: 1 },
  { seed: 44, y: 0, h: 12, n: 2, k: 2 }, { seed: 52, y: 86, h: 12, n: 3, k: 1 }, { seed: 61, y: 104, h: 12, n: 2, k: 2 }, { seed: 77, y: 53, h: 12, n: 2, k: 2 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 50 + r() * 70;
    for (let k = 0; k < 5; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.25 + r() * 0.5), rx: 8 + r() * 16, ry: 1.1 + r() * 1.3 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v > 0.6) p.put(x, y, v > 0.82 ? '#2a2e60' : '#222758');
    else if (v > 0.22 && (v - 0.22) * 2.4 > bayer(x, y)) p.put(x, y, '#1b2050');
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The Sky Tower: shaft, the pod's stack of rings, the Sky Deck, pedestal and mast ================= */
// rows (see ST): mast 8-53, cap ring 54, pedestal cone 55-58, Sky Deck lip 59, its glass 60-61, under-lip 62, silver
// cone 63-68, pod roof 69-70, SkyWalk glass rail 71-72, the SkyWalk ring 73-74 (the widest, 23 px), the restaurant's
// dark glazed band 75-76, lower lip 77-78, observation windows 79-81, white 82, dark band 83-86, the lower pod 87-93,
// its flare 94-96, the shaft (9 px) from 97 down to the foot, hidden behind the Viaduct from ST.seen.
const TW = { hi: '#fbfaff', lt: '#e6e4f6', md: '#bcbad6', sh: '#8a88aa', dk: '#5e5c7e', ink: '#24223a' };
const SHAFT = ['#4a4868', '#605e7e', '#7a7898', '#9896b4', '#b6b4ce', '#d2d0e6', '#e8e6f6'];
const SILVER = ['#5c5880', '#7a76a0', '#9a96bc', '#bab6d6', '#d6d2ec', '#ece8f8'];
const POD = ['#6a6888', '#8c8aa8', '#b0aec8', '#d0cee4', '#e8e6f6', '#f8f7ff'];
function renderTower() {
  const p = painter(W, H), put = p.put, cx = ST.cx;
  // a centred row of odd width w, shaded as a cylinder lit from the front-left
  const row = (y, w, pal, bias = 0) => {
    const hw = w >> 1;
    for (let i = -hw; i <= hw; i++) {
      const s = hw ? i / hw : 0, v = 0.92 - s * s * 0.5 - s * 0.16 + bias + (bayer(cx + i, y) - 0.5) * 0.08;
      put(cx + i, y, ramp(pal, v, cx + i, y));
    }
  };
  // the mast: a lattice foot, a slimmer middle with dishes, the thin top
  for (let y = 40; y <= 53; y++) { put(cx - 1, y, y & 1 ? '#d8d0f2' : '#b0a8d6'); put(cx, y, y % 3 ? '#9a92c2' : '#ece6fc'); put(cx + 1, y, y & 1 ? '#8078a8' : '#6a6290'); }
  for (let y = 24; y < 40; y++) { put(cx, y, '#e2dcf8'); put(cx - 1, y, y % 4 === 0 ? '#bcb4e0' : null); }
  for (const [y, s] of [[28, -1], [29, -1], [33, 1], [34, 1], [44, 2], [45, 2], [47, -2]]) put(cx + s, y, '#a8a0d0');
  for (let y = ST.tip + 1; y < 24; y++) put(cx, y, y < 14 ? '#c8c0e8' : '#d6d0f4');
  put(cx, ST.tip, '#9a92c2');
  // the cap ring and the pedestal cone
  row(54, 7, POD, 0.1);
  [5, 5, 7, 7].forEach((w, k) => row(55 + k, w, SILVER, 0.05));
  // the Sky Deck: a lit lip, its glass band with lit panes, an under-lip
  row(59, 13, POD, 0.15);
  for (const y of [60, 61]) for (let i = -5; i <= 5; i++) put(cx + i, y, (i + y) % 2 ? '#2c2a46' : (Math.abs(i) < 4 ? '#d6d0f0' : '#7a76a0'));
  row(62, 11, SILVER, -0.15);
  // the silver cone, 9 px under the Sky Deck to 11 at the pod's roof
  for (let y = 63; y <= 68; y++) row(y, y < 66 ? 9 : 11, SILVER, (y - 63) * 0.02);
  // the pod's roof and the SkyWalk's glass rail (posts every 3 px, a lit top rail)
  row(69, 11, SILVER, -0.1); row(70, 13, POD, -0.05);
  for (let i = -10; i <= 10; i++) { put(cx + i, 71, '#d8d6f2', 150); put(cx + i, 72, i % 3 === 0 ? '#c8c6e6' : '#6a6890', i % 3 === 0 ? 255 : 110); }
  // the SkyWalk ring: a bright rim, then its ribbed underside (the radial struts)
  row(73, 23, POD, 0.25);
  for (let i = -11; i <= 11; i++) put(cx + i, 74, Math.abs(i) > 10 ? '#8a88aa' : (i & 1) ? '#c8c6e2' : '#5a5878');
  // the restaurant's dark glazed band, its windows lit warm
  for (const y of [75, 76]) for (let i = -9; i <= 9; i++) {
    const win = (i + 30) % 3 === 1 && Math.abs(i) < 9;
    put(cx + i, y, win ? (y === 75 ? '#f6d8a0' : '#e8b878') : Math.abs(i) === 9 ? '#4a4866' : (y === 75 ? '#2e2c48' : '#22203a'));
  }
  // the lower lip and its shadow
  row(77, 17, POD, 0.2); row(78, 15, ['#3a3856', '#4a4866', '#5e5c7e'], 0);
  // the observation windows: lit glazing between thin mullions
  for (let y = 79; y <= 81; y++) for (let i = -6; i <= 6; i++) {
    const mul = (i + 30) % 3 === 0 || Math.abs(i) === 6;
    put(cx + i, y, mul ? (i < 0 ? '#b8b6d2' : '#7e7c9e') : y === 79 ? '#c8c0dc' : (i < 2 ? '#f4ead2' : '#d8ccb4'));
  }
  // white band, dark band, the lower pod
  row(82, 13, POD, 0.1);
  for (let y = 83; y <= 86; y++) row(y, 13, ['#14122a', '#1e1c34', '#2a2842', '#3a3854'], y === 83 ? 0.1 : -0.1);
  for (let y = 87; y <= 93; y++) row(y, 13, POD, 0.02 - (y - 87) * 0.02);
  // the flare down to the shaft
  row(94, 13, POD, -0.12); row(95, 11, SHAFT, 0.02); row(96, 11, SHAFT, 0);
  // the shaft: lit cool white, two columns of window slits (a few lit), the floodlight brightening low down
  for (let y = 97; y <= ST.foot; y++) {
    row(y, 9, SHAFT, (y - 97) / 300);
    const slit = (y - 97) % 5 < 2;
    if (slit) {
      put(cx - 2, y, hash(y, 3) < 0.22 ? '#f0e2c0' : '#3a3858');
      put(cx + 2, y, hash(y, 5) < 0.12 ? '#d8ccb0' : '#34324e');
    }
  }
  return p.done();
}

/* ================= The city centre: towers left of the Sky Tower, lower blocks to its right ================= */
// x0..x1 (exclusive), roof row, material, how its windows light, its crown, the night haze on it (dk)
const GLASS = ['#0e1428', '#141c34', '#1c2642', '#263250', '#323e60'];
const STONE = ['#2a2c48', '#3a3c5a', '#4e506e', '#666884', '#82849e'];
const GOLDG = ['#2a2018', '#4a3622', '#6e5230', '#9a7840', '#c8a058'];
const CITY = [
  // the far row, dimmer through the haze
  { x0: 0, x1: 20, top: 114, mat: STONE, win: 'grid', dk: 0.45, seed: 1 },
  { x0: 134, x1: 166, top: 118, mat: GLASS, win: 'floors', crown: 'step', dk: 0.45, seed: 2 },
  { x0: 232, x1: 266, top: 124, mat: GLASS, win: 'floors', dk: 0.45, seed: 4 },
  { x0: 312, x1: 330, top: 124, mat: STONE, win: 'grid', crown: 'step', dk: 0.42, seed: 5 },
  { x0: 330, x1: 362, top: 132, mat: GLASS, win: 'floors', dk: 0.42, seed: 6 },
  // the towers, back to front, with the tops they are known by
  { x0: 4, x1: 22, top: 102, mat: GLASS, win: 'floors', crown: 'curve', dk: 0.18, seed: 11 },          // a curved residential tower
  { x0: 20, x1: 38, top: 124, mat: GLASS, win: 'floors', crown: 'strip', dk: 0.12, seed: 12 },         // the black tower's lit strip
  { x0: 40, x1: 70, top: 92, mat: STONE, win: 'grid', crown: 'box', dk: 0.14, seed: 13 },              // the white tower, its rooftop box
  { x0: 72, x1: 93, top: 68, mat: GLASS, win: 'floors', crown: 'slant', dk: 0.08, seed: 14 },          // Commercial Bay's PwC Tower: the fin
  { x0: 82, x1: 104, top: 110, mat: GOLDG, win: 'floors', crown: 'band', dk: 0.1, seed: 15, warm: true },   // the gold AMP Centre
  { x0: 114, x1: 135, top: 100, mat: STONE, win: 'floors', crown: 'vero', dk: 0.12, seed: 16 },        // the Vero Centre's stepped crown
  { x0: 140, x1: 160, top: 124, mat: GLASS, win: 'grid', dk: 0.12, seed: 17 },
  { x0: 166, x1: 185, top: 90, mat: GLASS, win: 'floors', crown: 'round', dk: 0.1, seed: 18 },         // the ANZ Centre
  { x0: 184, x1: 236, top: 128, mat: STONE, win: 'grid', dk: 0.14, seed: 20 },                         // Customs Street: the billboard's roof
  { x0: 234, x1: 272, top: 136, mat: GLASS, win: 'floors', dk: 0.1, seed: 21 },
  { x0: 312, x1: 342, top: 130, mat: STONE, win: 'grid', dk: 0.1, seed: 22 },
  { x0: 342, x1: 372, top: 134, mat: STONE, win: 'grid', dk: 0.16, seed: 23 },
];
const HAZE = '#232456';
const WINS = [];                                  // windows that switch on and off, per frame
const FLOORS = [];                                // whole office floors whose lights go off and on (the cleaners' rounds)
function drawTowerBlock(p, b) {
  const put = p.put, M = b.mat.map(c => mixHex(c, HAZE, b.dk)), base = FAR_WL;
  const r = rng(b.seed * 31), w = b.x1 - b.x0;
  const cool = mixHex('#c6d0ec', HAZE, b.dk * 0.8), warm = mixHex(b.warm ? '#ffd890' : '#f0d8a8', HAZE, b.dk * 0.8);
  const roofAt = i => {                           // the crown's outline: the roof row at column i
    const u = (i - b.x0) / Math.max(1, w - 1);
    if (b.crown === 'slant') return b.top + (u < 0.32 ? Math.round(Math.pow(u / 0.32, 1.6) * 7) : u < 0.72 ? 7 : 10);   // the fin, then a stepped roof
    if (b.crown === 'curve') return b.top + Math.round(Math.pow(u, 2) * 9);
    if (b.crown === 'vero') return b.top + (u < 0.4 ? Math.round(Math.pow(u / 0.4, 2) * 5) : u < 0.7 ? 6 : 9);           // rising to the left in steps
    if (b.crown === 'box') return u > 0.2 && u < 0.8 ? b.top : b.top + 6;                                             // a set-back rooftop box
    if (b.crown === 'step') return b.top + (u < 0.18 || u > 0.82 ? 4 : 0);
    return b.top;
  };
  for (let i = b.x0; i < b.x1; i++) {
    const top = roofAt(i), u = (i - b.x0) / Math.max(1, w - 1);
    for (let y = top; y < base; y++) {
      let v = 0.55 - u * 0.35 + (bayer(i, y) - 0.5) * 0.12 + (y - top) / 400;
      if (b.crown === 'round') v = 0.75 - Math.abs(u - 0.38) * 1.2 + (bayer(i, y) - 0.5) * 0.12;
      let col = ramp(M, v, i, y);
      if (y === top) col = M[4];
      if (i === b.x0) col = M[3];
      put(i, y, col);
    }
  }
  // windows: an office's lit floors, or a block's grid of windows
  const floors = Math.floor((base - b.top - 4) / 3);
  for (let f = 0; f < floors; f++) {
    const y = b.top + 4 + f * 3, on = r() < (b.win === 'floors' ? 0.55 : 0.45);
    for (let i = b.x0 + 1; i < b.x1 - 1; i++) {
      if (y <= roofAt(i) + 2) continue;
      if (b.win === 'floors') {
        if (i === b.x0 + 1 && b.dk < 0.3 && hash(b.seed, f, 21) < 0.35) FLOORS.push({ x0: b.x0 + 1, x1: b.x1 - 1, y, b, on: b.warm || hash(b.seed, f) < 0.3 ? warm : cool, k: 6 + Math.floor(hash(f, b.seed, 22) * 4) * 2, s: hash(b.seed, f, 23) });
        if (!on || (i - b.x0) % 4 === 3) continue;
        put(i, y, hash(b.seed, i >> 2, f) < 0.8 ? (b.warm || hash(b.seed, f) < 0.3 ? warm : cool) : null);
      } else if ((i - b.x0) % 2 === 1) {
        const h1 = hash(b.seed, i, f);
        put(i, y, h1 < 0.32 ? warm : h1 < 0.44 ? cool : null);
        if (h1 > 0.6 && h1 < 0.72) WINS.push({ x: i, y, on: warm, off: M[1], k: 20 + Math.floor(hash(i, f, 7) * 3) * 10, s: hash(f, i, 9) });
      }
    }
  }
  // crowns: Commercial Bay's lit sloping edge and fin, the Vero Centre's lit stepped top, the ANZ Centre's blue band
  if (b.crown === 'slant') {
    for (let i = b.x0; i < b.x1; i++) { put(i, roofAt(i), '#b8c4e8'); put(i, roofAt(i) + 1, '#5a6a98'); }
    for (let y = b.top; y < base; y++) put(b.x0, y, '#8a9ac8');                          // the fin's lit leading edge
    for (let y0 = b.top + 12; y0 < base - 12; y0 += 14) { p.line(b.x0 + 1, y0, b.x1 - 2, y0 + 13, '#5e6e9e', 150); p.line(b.x1 - 2, y0, b.x0 + 1, y0 + 13, '#5e6e9e', 150); }
  } else if (b.crown === 'vero') {
    for (let i = b.x0; i < b.x1; i++) { put(i, roofAt(i), '#eef0fa'); put(i, roofAt(i) + 1, '#9aa4c8'); }
    for (let y = b.top; y < b.top + 12; y++) put(b.x0, y, '#e0e4f4');
  } else if (b.crown === 'round') {
    for (let y = b.top; y < b.top + 4; y++) for (let i = b.x0 + 1; i < b.x1 - 1; i++) put(i, y, y === b.top ? '#2a3a7a' : ramp(['#2a44a8', '#3a5ad0', '#5a7ae8'], 0.9 - Math.abs((i - b.x0) / w - 0.4) * 1.6, i, y));
  } else if (b.crown === 'curve') {
    for (let i = b.x0; i < b.x1; i++) put(i, roofAt(i), '#9aa8d8');
  } else if (b.crown === 'strip') {
    for (let y = b.top; y < base; y++) put(b.x0 + Math.round(w * 0.66), y, y % 4 ? '#c8d4f4' : '#8a98c8');
    for (let i = b.x0; i < b.x1; i++) put(i, b.top, '#aab8e0');
  } else if (b.crown === 'box') {
    for (let i = b.x0 + Math.round(w * 0.3); i < b.x0 + Math.round(w * 0.7); i++) for (let y = b.top + 2; y < b.top + 5; y++) put(i, y, '#e8ecf8');   // its lit panel
  } else if (b.crown === 'band') {
    for (let i = b.x0; i < b.x1; i++) { put(i, b.top, '#e8c070'); put(i, b.top + 1, '#2a1e14'); put(i, b.top + 2, '#2a1e14'); put(i, b.top + 3, '#c89a50'); }
  }
  // a red aircraft lamp on the tall ones (blinking ones are drawn per frame)
}
function renderCity() {
  const p = painter(W, H);
  for (const b of CITY) drawTowerBlock(p, b);
  return p.done();
}
const AIR_LAMPS = [[88, 75], [12, 100], [175, 89], [55, 91]];   // red lamps on the tall towers' roofs, blinking together

/* ================= The Viaduct Harbour: apartments round the basin's far end, the Eastern Viaduct, the Park Hyatt ================= */
// five- to seven-storey apartment blocks in cream, terracotta and grey render along the basin's south quay, their
// windows warm, balconies catching the promenade's lamps, bars open along the waterfront under them
const RENDERS = [['#3e3040', '#5a4650', '#7a6260', '#9a8070', '#b89c84'], ['#382630', '#54363a', '#744a46', '#925e52'],
  ['#2e2e44', '#42425a', '#5a5a74', '#76768e']];
const VIADUCT = [[140, 176, 146, 0], [176, 214, 142, 1], [214, 250, 148, 2], [250, 284, 145, 0], [284, 318, 144, 1],
  [318, 350, 147, 0], [350, 372, 143, 2]];
const PROM_LAMPS = [146, 160, 174, 190, 206, 222, 238, 254, 270, 314, 330, 346, 362];
function renderViaduct() {
  const p = painter(W, H), put = p.put;
  // the Eastern Viaduct's low bars and the maritime museum's sheds, far left, along the quay
  const r = rng(77);
  for (let x = 0; x < 140;) {
    const w = 10 + Math.floor(r() * 16), top = 148 + Math.floor(r() * 8), pal = RENDERS[Math.floor(r() * 3)].map(c => mixHex(c, HAZE, 0.25));
    for (let i = x; i < Math.min(140, x + w); i++) for (let y = top; y < FAR_WL + 2; y++) {
      let col = ramp(pal, 0.3 + (y - top) / 40 + (bayer(i, y) - 0.5) * 0.15, i, y);
      if (y === top) col = pal[pal.length - 1];
      if (y >= FAR_WL - 4 && y < FAR_WL - 1) col = (i + y) % 3 ? '#b88a58' : '#6a4a38';           // lit bar fronts
      put(i, y, col);
    }
    for (let y = top + 3; y < FAR_WL - 6; y += 3) for (let i = x + 2; i < Math.min(140, x + w) - 1; i += 3) if (hash(i, y, 7) < 0.4) put(i, y, '#e8c890');
    x += w;
  }
  // the apartments
  for (const [x0, x1, top, k] of VIADUCT) {
    const pal = RENDERS[k];
    for (let i = x0; i < x1; i++) for (let y = top; y < FAR_WL; y++) {
      const v = 0.12 + (y - top) / (FAR_WL - top) * 0.35 + (i === x0 ? 0.25 : 0) + (bayer(i, y) - 0.5) * 0.1;
      let col = mixHex(ramp(pal, v, i, y), HAZE, 0.3);
      if (y === top) col = pal[pal.length - 1];
      if (y === top + 1) col = pal[1];
      put(i, y, col);
    }
    for (let y = top + 3, f = 0; y < FAR_WL - 6; y += 3, f++) {
      for (let i = x0 + 2; i < x1 - 2; i += 3) {
        const h1 = hash(i, y, k + 3);
        put(i, y, h1 < 0.3 ? '#d8ac70' : h1 < 0.36 ? '#7a8cc0' : '#1c1626');
        put(i + 1, y, h1 < 0.3 ? '#c09060' : h1 < 0.36 ? '#64769e' : '#1c1626');
      }
      if (f % 2 === 0) for (let i = x0 + 1; i < x1 - 1; i++) if (hash(i >> 3, y, k) < 0.6) put(i, y + 1, mixHex(pal[2], HAZE, 0.25));   // balcony rails
    }
    for (let i = x0 + 3; i < x1 - 6; i += 9) for (let y = top - 3; y < top; y++) for (let j = 0; j < 5; j++) put(i + j, y, y === top - 3 ? pal[2] : pal[0]);   // rooftop pergolas
  }
  // the waterfront under them: bars' warm fronts, awnings, the promenade's edge
  for (let i = 140; i < 372; i++) {
    for (let y = FAR_WL - 5; y < FAR_WL - 1; y++) put(i, y, hash(i >> 3, 3) < 0.4 ? (y === FAR_WL - 5 ? ['#7a2e2a', '#24524a', '#8a6a2e', '#2e3a6a'][(i >> 3) & 3] : '#c8945a') : '#2e2630');
    put(i, FAR_WL - 1, '#6a5446');
  }
  for (const x of PROM_LAMPS) { put(x, FAR_WL - 7, '#fff0c8'); put(x, FAR_WL - 6, '#3a3040'); }
  return p.done();
}
/* ================= The rooftop billboard on Customs Street ================= */
// a steel frame on two posts on an office block's roof left of the Sky Tower, a catwalk and three lamps; the city's own ad is a
// waterfront cafe's flat white: a cup with a fern of latte art, FLAT WHITE, OPEN LATE
const BB_LAMPS = [0.18, 0.5, 0.82].map(f => BB.x + Math.round((BB.w - 1) * f));
function renderBillboard() {
  const p = painter(W, H), put = p.put, X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const cx of [BB.x + 8, BB.x + BB.w - 9]) for (let y = Y1 + 1; y < 128; y++) { put(cx, y, '#3a3a50'); put(cx + 1, y, '#16161e'); }
  for (let x = X0 - 1; x <= X1 + 1; x++) { put(x, Y1 + 2, '#2e2e40'); if (x % 3 === 0) put(x, Y1 + 3, '#24242e'); }   // the catwalk
  for (let x = X0; x <= X1; x++) { put(x, Y0, '#14141c'); put(x, Y1, '#14141c'); put(x, Y0 + 1, '#2e3044'); put(x, Y1 - 1, '#26283a'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#14141c'); put(X1, y, '#14141c'); put(X0 + 1, y, '#2a2c3e'); put(X1 - 1, y, '#16161e'); }
  for (const lx of BB_LAMPS) {
    for (let y = Y0 - 4; y < Y0; y++) put(lx, y, '#3a3a4c');
    for (let i = lx - 2; i <= lx + 2; i++) put(i, Y0 - 5, i === lx - 2 ? '#4a4a5c' : '#2a2a36');
  }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + x - lx))) / 10;
    put(x, y, ramp(['#2a1c16', '#3a281e', '#4e3626', '#644630'], clamp(0.9 - y / h * 0.5 - lamp * 0.3, 0, 1), x, y));
  }
  // the cup and saucer, the latte art, two wisps of steam
  const CUP = ['..wwwwwwwww..', '.wcccccccccw.', '.wccfcfcfccw.w', '.wcccfffcccww.w', '..wcccfcccw..w', '..wwcccccww.w.', '...wwwwwww...', 'sssssssssssss'];
  CUP.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const ch = row[i]; if (ch !== '.') put(3 + i, 5 + j, ch === 'w' ? '#f4ece0' : ch === 'c' ? '#b8784a' : ch === 'f' ? '#f8e8d0' : '#d8ccbc'); } });
  for (const [x0, ph] of [[6, 0], [10, 1.6]]) for (let j = 1; j <= 4; j++) put(x0 + Math.round(Math.sin(j * 1.2 + ph)), 5 - j, j < 3 ? '#a89888' : '#7a6a60');
  const c = p.done();
  sign(p.x, 'FLAT', 19 + Math.round((28 - signWidth('FLAT')) / 2), 2, '#f4ece0');
  sign(p.x, 'WHITE', 19 + Math.round((28 - signWidth('WHITE')) / 2), 8, '#f4ece0');
  sign(p.x, 'OPEN LATE', Math.round((w - signWidth('OPEN LATE')) / 2), 14, '#e8a860');
  return gradeAd(c, { level: 0.86, tint: '#141a3a', mix: 0.1, wear: 0.08, seed: 37 });
}

/* ================= The Viaduct Basin and the channel ================= */
const renderWater = () => ditherFill(W, EDGE - FAR_WL + 2, ['#0c1a3c', '#0a1634', '#08122c', '#070e24', '#050b1c'], (i, y) => y / (EDGE - FAR_WL + 2) * 0.8 + 0.12);
// the breeze's ripples: sparse light dashes, tileable, longer and brighter nearer; two drift across at their own speeds
function renderRipples(seed) {
  const h = EDGE - FAR_WL, p = painter(W, h), r = rng(seed);
  for (let k = 0; k < 760; k++) {
    const y = Math.floor(Math.pow(r(), 0.7) * h), len = 1 + Math.floor((y / h) * 4 + r() * 2), x = Math.floor(r() * W);
    for (let i = 0; i < len; i++) p.put((x + i) % W, y, y / h > 0.6 ? '#4a5a90' : '#34427a');
  }
  return p.done();
}
// moored along the far quay and the Halsey Street side: superyachts (white, two or three decks, lit saloons) and
// sailing yachts with tall masts and their anchor lights
const MOORED = [
  { x: 144, w: 30, kind: 'super', wl: FAR_WL }, { x: 184, w: 16, kind: 'sail', mast: 28, wl: FAR_WL },
  { x: 210, w: 28, kind: 'super', wl: FAR_WL }, { x: 260, w: 14, kind: 'sail', mast: 22, wl: FAR_WL },
  { x: 314, w: 16, kind: 'sail', mast: 30, wl: FAR_WL },
  { x: 60, w: 16, kind: 'sail', mast: 36, wl: 173 }, { x: 88, w: 24, kind: 'super', wl: 173 },
];
function drawYacht(p, b) {
  const put = p.put, { x, w, wl } = b, hh = b.big ? 4 : 3;
  for (let i = 0; i < w; i++) {                                  // the hull: white, a dark boot line, a raked bow on the right
    const bow = i > w - 4 ? i - (w - 4) : 0;
    for (let y = wl - hh; y < wl; y++) if (y - (wl - hh) >= bow - 1) put(x + i, y, y === wl - 1 ? '#262a40' : y === wl - hh ? '#b4b8d0' : '#8a8eaa');
  }
  if (b.kind === 'super') {                                       // decks stepping up and back, saloon windows lit
    const decks = b.big ? 3 : 2;
    for (let d = 0; d < decks; d++) {
      const y0 = wl - hh - 3 * (d + 1), a = Math.round(w * (0.12 + d * 0.12)), z = Math.round(w * (0.82 - d * 0.1));
      for (let i = a; i < z; i++) for (let y = y0; y < y0 + 3; y++) {
        const win = y === y0 + 1 && i > a && i < z - 1;
        put(x + i, y, win ? (hash(i, d, b.x) < 0.55 ? '#e0bc80' : '#3a4060') : y === y0 ? '#aeb2ca' : '#7e829e');
      }
    }
    put(x + Math.round(w * 0.5), wl - hh - 3 * decks - 2, '#d8d8e8'); put(x + Math.round(w * 0.5), wl - hh - 3 * decks - 1, '#9a9ab8');   // radar arch
  } else {
    for (let i = 3; i < w - 5; i++) put(x + i, wl - hh - 1, i < 6 || i > w - 8 ? '#6e728e' : '#9a9eb8');   // the cabin top
    const mx = x + Math.round(w * 0.42);
    for (let y = wl - hh - b.mast; y < wl - hh; y++) put(mx, y, y < wl - hh - b.mast + 3 ? '#9a9ab4' : '#626480');
    for (const f of [0.35, 0.62]) { const y = wl - hh - Math.round(b.mast * f); put(mx - 1, y, '#7a7c98'); put(mx + 1, y, '#7a7c98'); }
    p.line(mx, wl - hh - b.mast, x + w - 2, wl - hh - 1, '#5a5c78', 120);                       // the forestay
    p.line(mx, wl - hh - b.mast, x + 1, wl - hh - 1, '#4a4c68', 100);                            // the backstay
  }
}
function renderMoored(far) {                                     // far: the ones at the far quay, mirrored with the skyline
  const p = painter(W, H);
  for (const b of MOORED) if ((b.wl === FAR_WL) === far) drawYacht(p, b);
  return p.done();
}
// the anchor lights at the mastheads (twinkling a little on the swell), per frame
const MASTHEADS = MOORED.filter(b => b.kind === 'sail').map(b => ({ x: b.x + Math.round(b.w * 0.42), y: b.wl - 3 - b.mast - 1 }));

/* ================= Te Wero Island: the bridge's far landing, low, with a few trees and lamps ================= */
function renderTeWero() {
  const p = painter(W, H), put = p.put;
  for (let i = 56; i < 150; i++) for (let y = 164; y < 170; y++) put(i, y, y === 164 ? '#4a4a60' : y === 169 ? '#14141e' : ramp(['#16161f', '#20202e', '#2c2c3c'], 0.6 - (y - 164) / 8, i, y));
  const r = rng(19);
  for (let k = 0; k < 7; k++) {                                    // native trees: dark rounded crowns
    const cx = 60 + r() * 70, cy = 159 + r() * 2, rx = 4 + r() * 3, ry = 3 + r() * 2;
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++)
      if (((i - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1) put(i, y, (y - cy) < -ry * 0.3 && bayer(i, y) > 0.45 ? '#2a3a34' : '#141e1c');
  }
  for (const lx of [70, 96, 122]) { for (let y = 157; y < 164; y++) put(lx, y, '#2a2a38'); put(lx, 156, '#fff0c8'); }
  return p.done();
}

/* ================= Wynyard Crossing: the fixed spans, the bascule piers, the leaves, the A-frame masts ================= */
// a white footbridge on slim steel trestles; the two 22 m leaves pivot on grey steel piers and meet over the channel;
// a white A-frame mast on each pier carries a fan of cables to its leaf and a backstay; blue LEDs run up the masts, the
// handrail glows. When it opens the leaves swing up nearly upright and the masts tilt back.
const LEAF_L = 45, PIV_Y = 170;
const BRIDGE_LIT = '#eceaff', RAIL = '#c8c8de', DECKC = '#8a8aa6', FASCIA = '#d4d4ea', STEEL = ['#262838', '#3a3c54', '#55576e', '#72748c'];
function renderBridgeFixed() {
  const p = painter(W, H), put = p.put;
  const span = (a, b) => {
    for (let x = a; x < b; x++) {
      const dy = deckY(x);
      put(x, dy - 4, BRIDGE_LIT);                                   // the lit handrail
      if (x % 2 === 0) for (let y = dy - 3; y < dy; y++) put(x, y, RAIL, 170);
      put(x, dy, DECKC); put(x, dy + 1, FASCIA); put(x, dy + 2, '#3a3a52');
    }
    for (let x = a + 6; x < b - 2; x += 22) {                     // trestles down to the water, cross-braced
      const dy = deckY(x), wl = dy + 12;
      for (let y = dy + 3; y < wl; y++) { put(x, y, STEEL[2]); put(x + 4, y, STEEL[1]); }
      p.line(x, dy + 3, x + 4, wl - 1, STEEL[1]); p.line(x + 4, dy + 3, x, wl - 1, STEEL[0]);
    }
  };
  span(BR.x0, BR.PE - 6); span(BR.PW + 6, BR.x1);
  // the bascule piers: tapering grey steel caissons with a lattice, standing in the channel
  for (const px0 of [BR.PE, BR.PW]) {
    const dy = deckY(px0);
    for (let y = dy - 1; y < dy + 13; y++) {
      const hw = Math.round(8 - (y - dy) * 0.3);
      for (let i = -hw; i <= hw; i++) {
        let col = ramp(STEEL, 0.75 - (i + hw) / (2 * hw) * 0.55 + (bayer(px0 + i, y) - 0.5) * 0.1, px0 + i, y);
        if ((i + y) % 6 === 0 || (i - y) % 6 === 0) col = STEEL[3];
        put(px0 + i, y, col);
      }
    }
    for (let i = -8; i <= 8; i++) { put(px0 + i, dy - 2, BRIDGE_LIT); put(px0 + i, dy - 1, '#9a9ab8'); }
  }
  // the operator's cabin on the west pier's landing side: a small glass box, lit, the operator at the desk
  const cx0 = BR.PW + 10, cy = deckY(cx0);
  for (let y = cy - 9; y < cy - 1; y++) for (let i = cx0; i < cx0 + 8; i++) {
    const frame = i === cx0 || i === cx0 + 7 || y === cy - 9;
    put(i, y, frame ? '#6a6c88' : y < cy - 5 ? '#a8b8dc' : '#6a7aa8');
  }
  for (let i = cx0 - 1; i <= cx0 + 8; i++) put(i, cy - 10, '#9a9ab8');
  for (const [i, y, c] of [[cx0 + 3, cy - 7, '#22222e'], [cx0 + 3, cy - 6, '#2a3450'], [cx0 + 4, cy - 6, '#2a3450'], [cx0 + 3, cy - 5, '#2a3450'], [cx0 + 4, cy - 5, '#2a3450']]) put(i, y, c);
  return p.done();
}
// the moving parts, at leaf angle th (0 shut, about 1.3 open): side -1 the east (left) leaf, +1 the west
function leafGeom(side, th) {
  const px0 = side < 0 ? BR.PE + 3 : BR.PW - 3, dx = -side * Math.cos(th), dy = -Math.sin(th);
  return { px0, py0: PIV_Y, dx, dy, nx: side < 0 ? -dy : dy, ny: side < 0 ? dx : -dx };
}
function drawLeaf(c, side, th) {
  const g = leafGeom(side, th);
  const at = (k, j) => [Math.round(g.px0 + g.dx * k + g.nx * j), Math.round(g.py0 + g.dy * k + g.ny * j)];
  for (let k = 0; k <= LEAF_L; k++) {
    for (const [j, col] of [[0, DECKC], [1, FASCIA], [2, '#3a3a52']]) { const [x, y] = at(k, j); c.fillStyle = col; c.fillRect(x, y, 1, 1); }
    if (th > 0.2) { const [x, y] = at(k, 1); c.fillStyle = k % 6 < 3 ? '#fff4d8' : FASCIA; c.fillRect(x, y, 1, 1); }   // the leaf's lit edge
    if (k % 2 === 0) for (let j = -3; j < 0; j++) { const [x, y] = at(k, j); c.fillStyle = rgbaOf(RAIL, 0.66); c.fillRect(x, y, 1, 1); }
    const [x, y] = at(k, -4); c.fillStyle = BRIDGE_LIT; c.fillRect(x, y, 1, 1);
  }
  return g;
}
const MAST_H = [53, 57], MAST_LEAN = 0.07;
function drawMast(c, side, th) {
  const i = side < 0 ? 0 : 1, bx = side < 0 ? BR.PE : BR.PW, by = deckY(bx) - 2, a = MAST_LEAN + th * 0.55;
  const ax = Math.round(bx + side * Math.sin(a) * MAST_H[i]), ay = Math.round(by - Math.cos(a) * MAST_H[i]);
  const ln = (x0, y0, x1, y1, col, al = 1) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    c.fillStyle = al < 1 ? rgbaOf(col, al) : col;
    for (let k = 0; k <= n; k++) c.fillRect(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), 1, 1);
  };
  // the cables first, behind the legs: a fan to the leaf, two backstays to the fixed deck
  const g = leafGeom(side, th);
  for (const f of [0.42, 0.62, 0.82, 1]) ln(ax, ay, g.px0 + g.dx * LEAF_L * f, g.py0 + g.dy * LEAF_L * f - 3, '#b8b8d8', 0.42);
  for (const d of [36]) ln(ax, ay, bx + side * d, deckY(bx + side * d) - 4, '#b8b8d8', 0.36);
  // the white back strut down to the landing side, then the two legs of the A, the inner one with its blue LED strip
  const sx = bx + side * Math.round(26 - th * 10);
  ln(sx, deckY(sx) - 3, ax, ay, '#d6d6e8'); ln(sx + side, deckY(sx) - 3, ax + side, ay + 1, '#9a9ab8');
  ln(bx - 2, by, ax, ay, '#e6e6f4'); ln(bx + 2, by, ax, ay, '#c8c8de');
  const n = Math.max(Math.abs(ax - bx), by - ay);
  for (let k = 2; k < n; k += 2) { c.fillStyle = k % 4 ? '#5a7cff' : '#8aa4ff'; c.fillRect(Math.round(lerp(bx - side * 2, ax, k / n)), Math.round(lerp(by, ay, k / n)), 1, 1); }
  c.fillStyle = '#c8d0ff'; c.fillRect(ax - 1, ay - 1, 3, 3); c.fillStyle = '#7a96ff'; c.fillRect(ax, ay, 1, 1);
  return { ax, ay };
}


/* ================= Karanga Plaza: the tidal steps, the boardwalk, a lounger, harakeke, the light pole ================= */
// The tidal steps run down from the plaza to the water between timber fingers; the nearer treads are taller. The
// boardwalk's boards run out towards the water. A giant timber lounger on its steel track, a planter of harakeke, two
// cable-drum tables with candles, the plaza's light pole and the festoon's timber pole.
const STEPS = { x0: 108, x1: 296, rows: [186, 188, 191, 194, 197, 201, 205] };   // tread edges, far (the water) to near
const LOUNGER = { x: 300, y: 203 };               // the harakeke and the native grasses, swayed by the breeze: sw -1, 0 or 1 bends the tips that way
function renderGrass(sw, part) {                    // part 0: the harakeke, 1: the bottom planter's grasses
  const p = painter(W, H), put = p.put;
  if (part === 0) { const r = rng(57);
    for (let k = 0; k < 44; k++) {
      const bx = 3 + r() * 52, len = 24 + r() * 30, lean = (r() - 0.5) * 1.2, w2 = 2 + (r() < 0.4 ? 1 : 0), give = 0.6 + r() * 0.8;
      for (let j = 0; j < len; j++) {
        const u = j / len, x = bx + lean * j * (0.4 + u * 0.8) + sw * give * u * u * 3, y = 206 - j;
        for (let q = 0; q < (u < 0.6 ? w2 : u < 0.85 ? 2 : 1); q++) put(x + q, y, q === 0 && u > 0.25 ? '#5a7a4c' : u > 0.85 ? '#2e4a30' : q === w2 - 1 ? '#16281c' : '#24402a');
      }
    }
    for (const [sx, top] of [[18, 138], [40, 148]]) {
      const lx = y => sx + Math.round((206 - y) * 0.06 + sw * Math.pow((206 - y) / 68, 2) * 2);
      for (let y = top; y < 206; y++) put(lx(y), y, '#3a2a20');
      for (let y = top; y < top + 14; y += 2) { put(lx(y) + 1, y, '#9a3a24'); put(lx(y) - 1, y + 1, '#b84a2a'); }
    }
  }
  if (part === 1) { const r = rng(58);
    for (let k = 0; k < 150; k++) {
      const bx = 2 + r() * 208, len = 8 + r() * 16, lean = (r() - 0.5) * 1.6, warm = Math.max(0, 1 - Math.abs(bx - LAMP.x) / 60), give = 0.5 + r();
      for (let j = 0; j < len; j++) put(bx + lean * j * (j / len) + sw * give * (j / len) ** 2 * 2.5, 250 - j, j > len - 3 ? (warm > 0.3 ? '#a89a5a' : '#6a7a4a') : j > len * 0.5 ? '#3a5232' : '#22361e');
    }
  }
  return p.done();
}
// the giant lounger in profile: its back at the left, the seat to x + 56
const POLE = { x: 318, top: 150 };                // the festoon's timber pole at the edge
const TABLES = [{ x: 252, y: 224 }, { x: 318, y: 232 }];   // cable-drum tables on the boardwalk, candles in jars
const VP = { x: 296, y: 150 };                    // the boards run towards the water and meet here
const WOOD = ['#1e1820', '#2a2026', '#3a2c2e', '#4e3a36', '#64483e', '#7c5a48', '#946c52', '#ae8460'];
function renderPlaza() {
  const p = painter(W, H), put = p.put, R0 = STEPS.rows;
  // the boardwalk first, everywhere below the edge: boards running out to the water, staggered joints, light pools
  for (let x = 0; x < W; x++) for (let y = EDGE; y < H; y++) {
    const d = y - VP.y, f = (x - VP.x) / (d * 0.11), board = Math.floor(f), edge = f - board < 0.12 / (d / 60);
    const seg = Math.floor(Math.log(d) * 3.2 + hash(board, 9) * 3), cut = Math.floor(Math.log(d - 1) * 3.2 + hash(board, 9) * 3) !== seg;
    const warm = Math.max(0, 1 - Math.hypot((x - 418) / 120, (y - 214) / 40)) * 0.65 + Math.max(0, 1 - Math.hypot((x - LAMP.x) / 70, (y - 208) / 24)) * 0.3
      + Math.max(0, 1 - Math.hypot((x - 330) / 60, (y - 210) / 16)) * 0.2 + TABLES.reduce((a, t) => a + Math.max(0, 1 - Math.hypot((x - t.x) / 26, (y - t.y - 4) / 9)) * 0.25, 0);
    let v = 0.24 + warm + (hash(board, seg, 5) - 0.5) * 0.12 - (y - EDGE) / 500 + (bayer(x, y) - 0.5) * 0.08;
    if (edge || cut) v -= 0.16;
    put(x, y, ramp(WOOD, v, x, y));
  }
  // the sea wall's timber edge beam either side of the steps
  for (let x = 0; x < W; x++) if (x < STEPS.x0 - 5 || x > STEPS.x1 + 5) { put(x, EDGE - 2, '#a08068'); put(x, EDGE - 1, '#5a4438'); put(x, EDGE, '#2a2026'); }
  // the tidal steps: treads down and away to the water, the last one awash, each nosing catching the light
  for (let k = 0; k < R0.length - 1; k++) for (let y = R0[k]; y < R0[k + 1]; y++) for (let x = STEPS.x0; x < STEPS.x1; x++) {
    const nose = y === R0[k + 1] - 1, warm = Math.max(0, 1 - Math.abs(x - 220) / 160) * 0.12;
    let col = nose ? (k === 0 ? '#5a6a98' : mixHex('#b8987a', '#3a3040', 0.4 - k * 0.06)) : ramp(['#1a1824', '#28242e', '#3a3238', '#4c4040', '#5e4e48'], 0.12 + k * 0.12 + warm + (bayer(x, y) - 0.5) * 0.1, x, y);
    if (k === 0 && !nose) col = hash(x >> 1, y, 3) < 0.45 ? '#4a5a8a' : '#222c50';                         // awash
    put(x, y, col);
  }
  for (const [fx, dir] of [[STEPS.x0 - 5, 1], [STEPS.x1 + 1, -1]]) for (let y = R0[0] - 7; y < EDGE + 1; y++) for (let i = 0; i < 4; i++) {   // timber fingers
    const c = i === (dir > 0 ? 0 : 3) ? '#b8946e' : i === (dir > 0 ? 3 : 0) ? '#1e1618' : (y + i) % 5 ? '#6a5040' : '#4a3830';
    put(fx + i, y, y === R0[0] - 7 ? '#c8a478' : c);
  }
  // the lounger's steel track and the light pole's footing
  for (const ty of [LOUNGER.y + 4, LOUNGER.y + 7]) for (let x = 296; x < PAV.x0; x++) { put(x, ty, '#8a8a9a'); put(x, ty + 1, '#2a2430'); }
  // the harakeke planter's box (its leaves sway, drawn per frame from renderGrass)
  for (let y = 206; y < 224; y++) for (let x = 0; x < 58; x++) put(x, y, y === 206 ? '#b08a64' : x === 57 ? '#2a2026' : ramp(WOOD, 0.5 - (y - 206) / 40, x, y));
  // bollards along the edge
  for (const bx of [86, 340]) for (let y = EDGE - 7; y < EDGE + 1; y++) for (let i = 0; i < 5; i++) put(bx + i, y, y === EDGE - 7 ? '#9a9ab0' : i === 0 ? '#5a5a6e' : i === 4 ? '#1e1e28' : '#3a3a4a');
  // the light pole: a tall slim mast with a lantern
  for (let y = LAMP.y + 4; y < 214; y++) { put(LAMP.x, y, '#2e2e3e'); put(LAMP.x + 1, y, '#14141c'); }
  for (let i = LAMP.x - 2; i <= LAMP.x + 3; i++) put(i, 213, '#24242e');
  for (let i = LAMP.x - 3; i <= LAMP.x + 4; i++) { put(i, LAMP.y - 1, '#24242e'); put(i, LAMP.y, '#fff2d0'); put(i, LAMP.y + 1, i === LAMP.x - 3 || i === LAMP.x + 4 ? '#24242e' : '#ffd890'); }
  for (let i = LAMP.x - 2; i <= LAMP.x + 3; i++) put(i, LAMP.y - 2, '#2e2e3e');
  // the festoon's timber pole
  for (let y = POLE.top; y < EDGE + 2; y++) { put(POLE.x, y, '#8a6a50'); put(POLE.x + 1, y, '#3a2a24'); }
  // a long planter of native grasses along the bottom left, framing the near deck (the grasses sway: renderGrass)
  for (let x = 0; x < 214; x++) for (let y = 250; y < H; y++) put(x, y, y === 250 ? '#b08a64' : y === 251 ? '#6a4c38' : x === 213 ? '#1e1618' : ramp(WOOD, 0.42 - (y - 250) / 50 + (x % 17 === 0 ? -0.15 : 0), x, y));
  // the cable-drum tables: a round top, its drum, a jar for the candle (lit per frame), two stools at the first
  for (const T of TABLES) {
    for (let i = -11; i <= 11; i++) { put(T.x + i, T.y, Math.abs(i) > 9 ? '#8a6448' : '#c8a070'); put(T.x + i, T.y + 1, '#d8b080'); put(T.x + i, T.y + 2, '#6a4a36'); }
    for (let y = T.y + 3; y < T.y + 12; y++) for (let i = -5; i <= 5; i++) put(T.x + i, y, i === -5 ? '#8a6448' : i === 5 ? '#3a2a22' : (y - T.y) % 3 === 0 ? '#4a3428' : '#6a4a36');
    for (let i = -11; i <= 11; i++) put(T.x + i, T.y + 12, '#3a2a24');
    for (let y = T.y - 4; y < T.y; y++) for (let i = 0; i < 3; i++) put(T.x - 1 + i, y, i === 1 ? '#f8e0a0' : '#c8b088');
  }
  for (const sx of [TABLES[0].x - 17, TABLES[0].x + 17]) for (let y = TABLES[0].y + 5; y < TABLES[0].y + 12; y++) for (let i = -3; i <= 3; i++) put(sx + i, y, y === TABLES[0].y + 5 ? '#c8a070' : Math.abs(i) === 3 ? '#4a3428' : null);
  return p.done();
}
// the giant lounger in profile, drawn over whoever lies on it: a slatted back curving into a long seat, on legs
function renderLounger() {
  const p = painter(W, H), put = p.put, { x, y } = LOUNGER;
  for (let k = 0; k < 58; k++) {
    const back = k < 14 ? Math.round(Math.pow((14 - k) / 14, 1.4) * 15) : 0, top = y - 3 - back;
    for (let j = 0; j < 3; j++) put(x + k, top + j, j === 0 ? '#e0b080' : j === 1 ? (k % 3 ? '#a87c56' : '#7a5a40') : '#4a3428');
  }
  for (const lx of [x + 8, x + 50]) for (let yy = y; y + 4 > yy; yy++) { put(lx, yy, '#4a3428'); put(lx + 1, yy, '#2a1e1a'); }
  return p.done();
}

/* ================= The stacked container pavilion: the near corner ================= */
// Two shipping containers stacked: below, a teal box opened along its side into a bar (the flap propped up as an
// awning, shelves of bottles, a chalk menu, a coffee machine, pendant bulbs, the counter and its stools); above, a
// rust-red box with a long lit window onto a lounge and a neon BAR sign; a railed roof deck on top.
function renderPavilion() {
  const p = painter(W, H), put = p.put, { x0, x1, roof, mid, floor } = PAV;
  const box = (y0, y1, pal) => {
    for (let x = x0; x < x1; x++) for (let y = y0; y < y1; y++) {
      const rib = (x - x0) % 3, edge = y === y0 || y === y1 - 1 || x === x0 || x === x0 + 1 || x === x1 - 1;
      const glow = Math.max(0, 1 - Math.hypot((x - 418) / 70, (y - (y1 - 8)) / 30)) * 0.2;
      let col = ramp(pal, 0.4 + (rib === 0 ? 0.18 : rib === 2 ? -0.15 : 0) + glow + (bayer(x, y) - 0.5) * 0.08, x, y);
      if (edge) col = y === y0 ? pal[pal.length - 1] : pal[0];
      put(x, y, col);
    }
    for (let y = y0 + 2; y < y1 - 2; y++) { put(x0 + 4, y, pal[0]); put(x0 + 8, y, pal[0]); }       // the end doors' locking bars
    for (const cy of [y0 + 1, y1 - 2]) for (const cx of [x0, x1 - 2]) { put(cx, cy, '#c8c0b0'); put(cx + 1, cy, '#c8c0b0'); }   // corner castings
  };
  box(roof, mid, ['#2e120e', '#4a1e16', '#6a2c20', '#8a3e2c', '#a8523a']);       // the upper box: rust red
  box(mid, floor, ['#0a2226', '#12343a', '#1a4650', '#265a64', '#367480']);       // the lower box: deep teal
  // the upper lounge: a long window, warm, plants and a lamp inside
  for (let y = roof + 10; y < mid - 12; y++) for (let x = x0 + 12; x < x0 + 84; x++) {
    const mull = (x - x0 - 12) % 18 === 0 || y === roof + 10 || y === mid - 13;
    let col = mull ? '#2a1612' : y < roof + 18 ? '#ffe2a8' : '#f2c27a';
    if (!mull && y > mid - 18 && hash(x >> 2, 7) < 0.4) col = '#3a5a32';                                         // pot plants on the sill
    put(x, y, col);
  }
  // its glass end facing the water, and the railed roof deck
  for (let y = roof + 3; y < mid - 3; y++) for (let x = x0 + 1; x < x0 + 3; x++) put(x, y, y % 6 ? '#e8b878' : '#6a3a24');
  for (let x = x0 - 1; x <= x1; x++) { put(x, roof - 7, '#e0d8d0'); put(x, roof - 1, '#8a8088'); if ((x - x0) % 6 === 0) for (let y = roof - 6; y < roof - 1; y++) put(x, y, '#b0a8a8'); }
  // the bar's flap, propped up as an awning, lit from below
  for (let x = x0 + 8; x < x1 - 8; x++) { put(x, mid + 3, '#0a1e22'); put(x, mid + 4, '#1e4a52'); put(x, mid + 5, '#2a6068'); put(x, mid + 6, '#f0c888'); }
  for (const sx of [x0 + 9, x1 - 10]) for (let y = mid + 7; y < mid + 12; y++) put(sx, y, '#3a3a44');                  // its stays
  // the lit interior: the back wall, shelves of bottles, the chalk menu, pendant bulbs on their cords
  const H0 = mid + 7, H1 = floor - 15;
  for (let y = H0; y < H1; y++) for (let x = x0 + 10; x < x1 - 10; x++) {
    let col = y < H0 + 7 ? '#ffe6b0' : '#f2c27a';
    if ((y === H0 + 6 || y === H0 + 12) && x > x0 + 34) col = '#8a5a30';                                          // shelves
    if ((y === H0 + 5 || y === H0 + 11) && x > x0 + 34 && x % 2 === 0) col = ['#6a8a4a', '#a83a2a', '#e8c060', '#4a6aa8', '#e8e0d0'][(x >> 1) % 5];   // bottles
    if (x >= x0 + 14 && x < x0 + 30 && y >= H0 + 3 && y < H0 + 15) col = (x === x0 + 14 || x === x0 + 29 || y === H0 + 3 || y === H0 + 14) ? '#6a4a30' : (hash(x, y, 4) < 0.18 ? '#d8d4c8' : '#26282a');   // the menu
    put(x, y, col);
  }
  for (const bx of [x0 + 42, x0 + 70]) { for (let y = H0; y < H0 + 3; y++) put(bx, y, '#3a2a20'); put(bx, H0 + 3, '#fff4d0'); put(bx - 1, H0 + 3, '#ffd890'); put(bx + 1, H0 + 3, '#ffd890'); }
  // the coffee machine on the counter's right end
  for (let y = H1 - 9; y < H1; y++) for (let x = x1 - 26; x < x1 - 14; x++) put(x, y, y === H1 - 9 ? '#e8e8f0' : x === x1 - 26 ? '#a8a8b8' : x === x1 - 15 ? '#5a5a66' : (y === H1 - 5 && x > x1 - 24 && x < x1 - 16) ? '#2a2a30' : '#c8c8d4');
  put(x1 - 18, H1 - 7, '#ff4a3a');
  // the counter, its front panel of timber slats, three stools
  for (let x = x0 + 8; x < x1 - 8; x++) { put(x, H1, '#f0d0a0'); put(x, H1 + 1, '#b8885a'); put(x, H1 + 2, '#4a3020'); for (let y = H1 + 3; y < floor; y++) put(x, y, (x - x0) % 4 === 0 ? '#3a2a20' : y < H1 + 5 ? '#8a6448' : '#6a4a36'); }
  for (const sx of [x0 + 24, x0 + 52, x0 + 80]) {
    for (let i = -3; i <= 3; i++) { put(sx + i, floor - 8, '#d8a878'); put(sx + i, floor - 7, '#8a6448'); }
    for (let y = floor - 6; y < floor; y++) { put(sx - 2, y, '#2a2a30'); put(sx + 2, y, '#2a2a30'); }
    put(sx - 2, floor - 3, '#4a4a54'); put(sx - 1, floor - 3, '#4a4a54'); put(sx, floor - 3, '#4a4a54'); put(sx + 1, floor - 3, '#4a4a54'); put(sx + 2, floor - 3, '#4a4a54');
  }
  const c = p.done();
  // the neon BAR sign on the upper box (FONT3 at 2x), and COFFEE on the flap
  const big = (s, X, Y, col) => { let cx = X; for (const ch of s) { const g = glyphOf(ch); g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.x.fillStyle = col, p.x.fillRect(cx + i * 2, Y + j * 2, 2, 2); }); cx += (g[0].length + 1) * 2; } };
  big('BAR', x0 + 87, roof + 12, '#ff9aa8');
  sign(p.x, 'COFFEE', x0 + 8 + Math.round((x1 - x0 - 16 - signWidth('COFFEE')) / 2), mid - 8, '#f4e4c0');
  return c;
}

/* ================= A pohutukawa in flower overhead: its trunk at the right edge, the canopy over the corner ================= */
// gnarled grey-brown limbs; leathery oval leaves in rosettes, grey-green with silvery undersides; the flowers are
// spiky crimson pompoms of stamens, gold-tipped, in clusters at the shoots' ends
const BLOOMS = [];
function renderTrunk() {
  const p = painter(W, H), put = p.put;
  for (let y = 40; y < PAV.floor; y++) {                          // the trunk, leaning in from the right edge
    const cx = 478 + Math.round(Math.sin(y * 0.05) * 1) - Math.round(Math.max(0, 80 - y) * 0.06), hw = 2 + (y > 180 ? 1 : 0);
    for (let i = -hw; i <= hw; i++) put(cx + i, y, i === -hw ? '#6a5a50' : i < 0 ? '#3e3230' : i === hw ? '#1e1618' : '#2e2426');
    if (y % 9 === 0) put(cx - 1, y, '#8a7466');
  }
  return p.done();
}
function renderBranch() {
  const p = painter(W, H), put = p.put, r = rng(88);
  const limbs = [[[474, 44], [456, 34], [438, 36], [420, 26], [402, 28], [386, 18]], [[456, 34], [448, 46], [436, 52]], [[470, 40], [474, 22], [484, 10]], [[438, 36], [430, 18], [422, 8]]];
  limbs.forEach((pts, li) => {
    for (let k = 1; k < pts.length; k++) {
      const [a, ay] = pts[k - 1], [b, by] = pts[k], wdt = Math.max(0.6, (li ? 2 : 3.4) - k * 0.55);
      for (let s = 0; s <= 1; s += 0.03) {
        const x = lerp(a, b, s), y = lerp(ay, by, s) + Math.sin(s * 3 + k) * 0.6;
        for (let j = -wdt; j <= wdt; j += 1) put(x, y + j, j < -wdt + 1 ? '#6a5a50' : j > wdt - 1.2 ? '#8a5a40' : '#2e2426');
      }
    }
  });
  const tips = [];
  for (const pts of limbs) for (let k = 1; k < pts.length; k++) for (let s = 0.15; s < 1; s += 0.28) tips.push([lerp(pts[k - 1][0], pts[k][0], s), lerp(pts[k - 1][1], pts[k][1], s)]);
  for (const [tx, ty] of tips) for (let k = 0; k < 16; k++) {     // leaf rosettes: grey-green ovals, silvery undersides
    const a = r() * TAU, d = 1 + r() * 8, x = Math.round(tx + Math.cos(a) * d * 1.3), y = Math.round(ty + 1 + Math.abs(Math.sin(a)) * d);
    const under = r() < 0.4, c1 = under ? '#8e9c8a' : r() < 0.5 ? '#3e5040' : '#4a5e4a', c2 = under ? '#6a7a68' : '#26342a';
    put(x, y, c1); put(x + 1, y, c1); put(x + 2, y, c2); put(x + 1, y + 1, c2);
  }
  for (const [tx, ty] of tips) if (r() < 0.6) {                   // the flower clusters: spiky crimson pompoms of stamens
    const n = 1 + Math.floor(r() * 2);
    for (let f = 0; f < n; f++) {
      const bx = Math.round(tx + (r() - 0.5) * 10), by = Math.round(ty - 1 + r() * 7);
      BLOOMS.push({ x: bx, y: by });
      put(bx, by, '#8a0e18'); put(bx + 1, by, '#a8141e'); put(bx, by + 1, '#a8141e');
      for (let k = 0; k < 12; k++) {                              // stamens radiating out, a gold anther on most
        const a = (k + r() * 0.6) / 12 * TAU, L = 2 + Math.round(r() * 2);
        for (let j = 1; j <= L; j++) put(bx + Math.round(Math.cos(a) * j), by + Math.round(Math.sin(a) * j * 0.85), j === L ? (r() < 0.6 ? '#f0c040' : '#ff5a5a') : j === 1 ? '#c8182a' : '#e8283a');
      }
    }
  }
  return p.done();
}

/* ================= People ================= */
const SKINS = ['#f0c8a2', '#d6a07a', '#a87452', '#6e4630'], HAIRS = ['#16100e', '#38221a', '#6a4426', '#c49a5a', '#2c2a32', '#bcb6b2'];
const PNIGHT = '#121838';
// a look for a person h px tall, dimmed for the night, with a line of light on the shoulders (warm by the bar, cool
// by the water)
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.24, m = c => mixHex(c, PNIGHT, dk), lit = o.lit ?? '#ffd8a0';
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 17 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], lit, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, lit, 0.32), bot: m(bot), shoe: m('#1e1820'), long: o.long || 0, apron: o.apron ? m(o.apron) : null,
    cap: o.cap ? m(o.cap) : null };
}
/**
 * One person, feet (or seat) on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (holding, offering), 5 hand to the face (a drink, chips); o.lean: head offset
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit && view === 1) {
    R(tx, fy + 1, 2, 4, P.bot); R(tx + bw - 2, fy + 1, 2, 4, P.bot); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit && side) {
    const tl = P.h >= 20 ? 6 : 5;
    R(dir > 0 ? x - 1 : x - tl + 2, fy - 1, tl, 2, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 2, fy + 1, 2, 4, P.bot);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);
  if (P.apron && view !== 0) R(side ? (dir > 0 ? x : x - 1) : tx + 1, ty + 2, side ? 2 : bw - 2, th + 1, P.apron);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) {
      R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
      const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018');
      if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe8c0'); R(p0, hy - 1, 2, 1, '#9ad0f0'); }
    } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(ax + (sg < 0 ? -1 : 1), hy, 1, ty - hy + 1, P.topD); R(ax + (sg < 0 ? -1 : 1), hy - 1, 1, 1, P.skin); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 5) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, hy + hh - 1, 1, 2, P.skin); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.cap) { R(hx, hy - 1, hw, 2, P.cap); R(side ? (dir > 0 ? hx + hw - 1 : hx - 1) : hx, hy + 1, side ? 2 : hw, 1, P.cap); }
  return hy;
}
const TOPS = ['#c8404a', '#e0b840', '#e8e2d4', '#3a64c0', '#2a9a8a', '#d87aa0', '#e0803a', '#4a8a4a', '#7a5ac0', '#c0c8d8', '#1e2230', '#5aa8d0'];
const BOTS = ['#2a3858', '#1e1c28', '#a8926a', '#d0ccc4', '#3a3a58', '#4a3a30'];
const COOL = '#b8c0ff';                            // the water side's light on shoulders and hair

/* ================= The cast: people who stay (placeholders at their final spots for the concept) =================
   rank: drawn while rank < crowd (Settings, People); rank 0 stays at every level above 0 (the bartender, the couple on
   the steps, the photographer); couples share one rank */
const rk = i => frac((i + 0.5) * 0.618);
const CAST = [
  { id: 'photo', x: 78, y: 203, rank: 0, P: look(22, 1, 0, '#2a9a8a', BOTS[0], { lit: COOL }) },
  { id: 'him', x: 156, y: 196, rank: 0, P: look(21, 2, 0, '#e8e2d4', BOTS[0], { lit: COOL }) },
  { id: 'her', x: 164, y: 196, rank: 0, P: look(20, 0, 3, '#c8404a', BOTS[1], { long: 4, lit: COOL }) },
  { id: 'kid', x: 214, y: 190, rank: 0.12, P: look(13, 1, 2, '#e0b840', BOTS[0], { lit: COOL }) },
  { id: 'mum', x: 226, y: 193, rank: 0.12, P: look(20, 1, 1, '#4a8a4a', BOTS[3], { long: 3, lit: COOL }) },
  { id: 'busk', x: 266, y: 204, rank: 0, P: look(22, 3, 0, '#8a2a34', BOTS[1], { cap: '#2a2a34' }) },
  { id: 'bar', x: PAV.x0 + 40, y: 197, rank: 0, P: look(21, 1, 4, '#1e2230', BOTS[1], { apron: '#3a2a22', dk: 0.05 }) },
  { id: 'barista', x: PAV.x0 + 84, y: 197, rank: 0, P: look(20, 0, 3, '#e8e2d4', BOTS[1], { apron: '#2a2a30', dk: 0.05, long: 3 }) },
  { id: 'stool1', x: PAV.x0 + 24, y: 198, rank: rk(3), P: look(21, 0, 2, '#3a64c0', BOTS[3], { long: 3 }) },
  { id: 'stool2', x: PAV.x0 + 80, y: 198, rank: rk(2), P: look(22, 2, 5, '#e0803a', BOTS[4]) },
  { id: 'tabA', x: TABLES[0].x - 16, y: TABLES[0].y + 4, rank: 0.3, P: look(25, 2, 1, '#5aa8d0', BOTS[2]) },
  { id: 'tabB', x: TABLES[0].x + 16, y: TABLES[0].y + 4, rank: 0.3, P: look(24, 0, 2, '#e0b840', BOTS[1], { long: 4 }) },
  { id: 'roof1', x: PAV.x0 + 44, y: PAV.roof - 1, rank: rk(9), P: look(20, 1, 1, '#c0c8d8', BOTS[0]) },
  { id: 'roof2', x: PAV.x0 + 51, y: PAV.roof - 1, rank: rk(9), P: look(19, 0, 3, '#d87aa0', BOTS[1], { long: 4 }) },
];
// the couple on the lounger, lying back: head on the backrest, legs along the seat
const LOUNGE = [{ x: LOUNGER.x + 4, P: look(22, 3, 0, '#c0c8d8', BOTS[5]), rank: rk(5) }, { x: LOUNGER.x + 9, P: look(21, 0, 3, '#7a5ac0', BOTS[1], { long: 4 }), rank: rk(5) }];
function recline(x, seat, P, phone) {
  x = Math.round(x);
  R(x + 13, seat - 4, 16, 3, P.bot); R(x + 29, seat - 5, 2, 3, P.shoe);                       // legs along the seat
  for (let k = 0; k < 10; k++) R(x + 3 + k, seat - 12 + Math.round(k * 0.8), 2, 5, P.top);     // the torso, leaning back
  R(x + 3, seat - 12, 10, 1, P.topL);
  R(x, seat - 17, P.hw, P.hh, P.skin); R(x, seat - 17, P.hw, 2, P.hair);                      // the head on the backrest
  if (P.long) R(x - 1, seat - 16, 1, P.long + 2, P.hair);
  if (phone) { R(x + 5, seat - 19, 1, 4, P.topD); R(x + 4, seat - 21, 3, 2, '#0e1018'); R(x + 4, seat - 20, 3, 1, '#9ad0f0'); }
}
function castPose(c, t) {
  const lift = t >= BRIDGE.up0 - 2 && t < BRIDGE.down1, show = showOn(t), kiss = frac((t + 20) / 40) > 0.88;
  switch (c.id) {
    case 'photo': return lift || show || frac((t + 4) / 24) < 0.6 ? { view: 0, aR: 2 } : { view: 2, dir: 1 };
    case 'him': return kiss ? { sit: true, view: 0, lean: 1 } : { sit: true, view: 0 };
    case 'her': return { sit: true, view: 0, lean: -1, aL: show && t < SHOW.r1 + 6 ? 4 : 0 };
    case 'kid': return wash(t) > 0.3 ? { sit: true, view: 1, aL: 4, aR: 4 } : t >= 70 && t < 90 ? { sit: true, view: 0, aR: 4 } : { sit: true, view: 0 };
    case 'mum': return { sit: true, view: 0, lean: wash(t) > 0.3 ? -1 : 0, aL: frac(t / 15) < 0.3 ? 2 : 0 };
    case 'busk': return { sit: true, view: 1, aR: 3, aL: 3, lean: step(t, 120) % 2 };
    case 'bar': return { view: 1, aR: serving(t) ? 3 : 0 };
    case 'barista': return { view: 2, dir: 1, aR: 3 };
    case 'stool1': return { sit: true, view: 0, aR: frac(t / 10) < 0.3 ? 5 : 3 };
    case 'stool2': return { sit: true, view: 0, aL: frac((t + 4) / 10) < 0.3 ? 5 : 3, lean: -1 };
    case 'tabA': return { sit: true, view: 2, dir: 1, aR: frac(t / 9) < 0.3 ? 5 : 3 };
    case 'tabB': return { sit: true, view: 2, dir: -1, aR: frac((t + 4) / 9) < 0.3 ? 5 : 3 };
    case 'roof1': return show ? { view: 2, dir: -1, aR: 3 } : { view: 2, dir: -1 };
    case 'roof2': return { view: 2, dir: -1, lean: -1 };
  }
  return {};
}
// walkers on the plaza's front row: each crosses a whole number of times a loop
const WALKERS = [
  { y: FEET, n: 3, ph: 0.15, dir: 1, speed: 16, rank: rk(6), P: look(24, 1, 2, '#5a8a5a', BOTS[0]), dog: true },
  { y: FEET, n: 4, ph: 0.6, dir: -1, speed: 20, rank: rk(7), P: look(24, 0, 5, '#e8e2d4', BOTS[4]), pair: look(23, 2, 0, '#3a64c0', BOTS[1]) },
  { y: FEET + 1, n: 6, ph: 0.35, dir: 1, speed: 44, rank: rk(10), P: look(24, 1, 0, '#e0803a', BOTS[1]), jog: true },
];
function walkerAt(w, t) {
  const P = LOOP / w.n, s = ((t + w.ph * P) % P + P) % P, dur = (W + 40) / w.speed;
  if (s >= dur) return null;
  return { x: w.dir > 0 ? -20 + s * w.speed : W + 20 - s * w.speed, y: w.y, dir: w.dir };
}
function drawDog(x, y, dir, t) {
  const st = step(t, 1536) % 2;
  R(x - 3, y - 4, 6, 3, '#c8a878'); R(dir > 0 ? x + 2 : x - 4, y - 6, 3, 3, '#d8b888'); R(dir > 0 ? x + 4 : x - 4, y - 7, 1, 1, '#8a6a48');
  R(x - 3 + st, y - 1, 1, 1, '#6a5038'); R(x + 1 + st, y - 1, 1, 1, '#6a5038'); R(dir > 0 ? x - 4 : x + 3, y - 5, 1, 1, '#c8a878');
}

/* ================= Auckland's own weather: pohutukawa stamens on the sea breeze, three depths ================= */
const STAMENS = [                                 // far ones over the water by the tree, mid ones past the bridge, near ones from the canopy
  { n: 12, cnt: 8, y0: 110, y1: 192, len: 1, a: 0.45, x0: 380, span: 100, drift: 90 },
  { n: 18, cnt: 10, y0: 50, y1: 212, len: 2, a: 0.8, x0: 370, span: 110, drift: 170 },
  { n: 24, cnt: 9, y0: 30, y1: 272, len: 3, a: 1, x0: 388, span: 92, drift: 230 },
];
const STAMEN_COLS = ['#e8283a', '#c01624', '#ff4a52'];
function drawStamens(depth, t) {
  const L = STAMENS[depth];
  for (let i = 0; i < L.cnt; i++) {
    const { cyc, u } = cycle(t, L.n, hash(i, depth, 61));
    const x = Math.round(L.x0 + hash(i, cyc, 62 + depth) * L.span - u * L.drift + 4 * Math.sin(u * 7 + i));
    const y = Math.round(lerp(L.y0, L.y1, u) + 3 * Math.sin(u * 11 + i * 1.3));
    const col = STAMEN_COLS[(i + cyc) % 3];
    ctx.globalAlpha = L.a * Math.min(1, u * 8, (1 - u) * 8);
    const tilt = step(t + i * 0.2, 960) % 3 - 1;                // a short crimson thread, turning as it falls, its anther gold
    for (let j = 0; j < L.len; j++) R(x + Math.round(tilt * j * 0.6), y - j, 1, 1, j === L.len - 1 && L.len > 2 ? '#f0c040' : col);
  }
  ctx.globalAlpha = 1;
}
const WEATHER = weatherPlans(['blossoms']);
const LIGHTS = [{ x: LAMP.x, y: LAMP.y + 4, r: 40 }, { x: 418, y: 184, r: 50 }, { x: 340, y: 154, r: 28 },
  { x: BR.PE - 3, y: 118, r: 12, col: '#a8b8ff' }, { x: BR.PW + 4, y: 114, r: 12, col: '#a8b8ff' }];
const LIGHTS_BB = [...LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 4, r: 18 }];
const WX_LAND = [[96, 132], [FAR_WL + 4, EDGE], [EDGE + 6, H + 4]];   // the far depth lands on the towers, above what the basin mirrors
// the near depth stays out of the bar's lit hatch, under its propped flap (the box lines up with the hatch's edges)
const WX_SHELTER = [[], [], [[PAV.x0 + 10, PAV.mid + 7, PAV.x1 - 11, PAV.floor - 16]]];
const WX = { land: WX_LAND, lights: LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.col })), shelter: WX_SHELTER };
const WX_BB = { ...WX, lights: LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.col })) };

/* ================= The loop's timeline (seconds) =================
   0-240    all loop: pohutukawa stamens drift down; windows switch on and off; the masts' blue LEDs breathe; the Sky Tower's
            red lamps blink; anchor lights twinkle; the festoon and the candles flicker; the busker strums; the bar serves
            a customer every 12 s (from the right, back along the front row with a coffee); people cross the bridge
   8-24     red-billed gulls cross, right to left               18-46   a water taxi crosses the basin behind the bridge
   30-46    two cyclists ride over the bridge                    40-50   a fish jumps in the channel (ring)
   56-108   BIG: Wynyard Crossing lifts. 56 the amber lamps flash and the bell rings, walkers wait at the barriers;
            60-76 both leaves swing up, the masts tilt back; 64-110 a sailing yacht motors out of the basin, its mast
            between the leaves (76-88), and away to the left; 92-106 the leaves come down; 108 the walkers go on.
            The photographer films it (58-100), the kid on the steps points (70-90)
   110-122  gulls                                               124-172 BIG: the dinner-cruise ferry comes in from the
            harbour, horn at 126, crosses in front of the bridge and goes behind the pavilion to its berth (horn 158);
            gulls follow it; its wash laps up the tidal steps at 150-162 and the kid pulls her feet up and laughs
   132-154  a plane crosses high, landing lights on              168-222 night kayakers paddle across the channel
   176-214  BIG: the Sky Tower's light show: 176-182 the pod's rings brighten; 182-194 violet rises up the shaft to the
            tip; 194-206 it turns magenta, teal and blue while a light runs up the mast; 206-214 back to its own white.
            She points (182-200), the photographer films, the rooftop couple turn to it
   216-232  gulls                                               220-236 a cyclist and walkers over the bridge
*/
const BRIDGE = { warn: 56, up0: 60, up1: 76, down0: 92, down1: 106, clear: 108 };
const YACHT = { t0: 64, gap: 76, past: 88, t1: 110 };
const FERRY = { t0: 124, t1: 172, wl: 185 };
const SHOW = { t0: 176, r0: 182, r1: 194, c1: 206, t1: 214 };
const GULLS = [[8, 24, -1, 46], [110, 122, 1, 58], [216, 232, -1, 40]];
const PLANE = { t0: 132, t1: 154 };
const TAXI = { t0: 18, t1: 46 };
const KAYAK = { t0: 168, t1: 222, wl: 183 };
const FISH = [44, 140, 204];
const leafAngle = t => 1.32 * (smooth(BRIDGE.up0, BRIDGE.up1, t) - smooth(BRIDGE.down0, BRIDGE.down1, t));
const lifting = t => t >= BRIDGE.warn && t < BRIDGE.clear;
function yachtAt(t) {
  if (t < YACHT.t0 || t >= YACHT.t1) return null;
  if (t < YACHT.gap) { const u = smooth(YACHT.t0, YACHT.gap, t); return { x: lerp(326, 314, u), y: lerp(160, 167, u), s: lerp(0.55, 0.78, u) }; }
  if (t < YACHT.past) { const u = (t - YACHT.gap) / (YACHT.past - YACHT.gap); return { x: lerp(314, 304, u), y: lerp(167, 182, u), s: lerp(0.78, 1, u) }; }
  const u = (t - YACHT.past) / (YACHT.t1 - YACHT.past);
  return { x: lerp(304, -50, u * u * 0.4 + u * 0.6), y: 182 + Math.min(1, u * 3), s: 1 };
}
function drawEventYacht(t, Y) {
  const s = Y.s, len = Math.round(30 * s), x0 = Math.round(Y.x - len / 2), wl = Math.round(Y.y), mast = Math.round(48 * s), hh = Math.max(2, Math.round(4 * s));
  ctx.globalAlpha = 0.3; R(x0, wl + 1, len, Math.max(1, hh >> 1), '#c8cce0'); ctx.globalAlpha = 1;
  for (let i = 0; i < len; i++) { const bow = i < 3 ? 3 - i : 0; R(x0 + i, wl - hh + bow, 1, hh - bow, i < len - 2 ? '#e0e2ee' : '#b8bcd0'); R(x0 + i, wl - 1, 1, 1, '#2a3048'); }
  R(x0 + Math.round(len * 0.35), wl - hh - 2, Math.round(len * 0.4), 2, '#c8ccdc');
  R(x0 + Math.round(len * 0.4), wl - hh - 1, Math.round(len * 0.3), 1, '#f8dca0');
  const mx = x0 + Math.round(len * 0.45);
  R(mx, wl - hh - mast, 1, mast, '#b8bcd4'); R(mx, wl - hh - Math.round(mast * 0.3), Math.round(len * 0.45), 1, '#8a90ac');
  R(mx, wl - hh - mast - 1, 1, 1, '#ffffff'); halo(HL.mastLight, mx, wl - hh - mast - 1, 0.7);
  R(x0, wl - hh, 1, 1, '#ff3a3a'); R(x0 + len - 1, wl - hh, 1, 1, '#3aff7a');
  ctx.fillStyle = '#c8d4e8';
  for (let k = 1; k <= 7; k++) { ctx.globalAlpha = 0.45 * (1 - k / 8); ctx.fillRect(Math.round(x0 + len + k * 2), wl - (k & 1), 2, 1); }
  ctx.globalAlpha = 1;
}

/* ================= The dinner-cruise ferry: a white catamaran, two lit decks ================= */
const FERRY_ROWS = [
  '..................kkkkkkkkkkkkkkkkkkkkkkkk..................',
  '.................kyyykyyykyyykyyykyyykyyyk..................',
  '................wwwwwwwwwwwwwwwwwwwwwwwwwwww................',
  '..........wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwk...........',
  '........wwyyyykyyyykyyyykyyyykyyyykyyyykyyyykyyww...........',
  '.......wwwyyyykyyyykyyyykyyyykyyyykyyyykyyyykyywww..........',
  '...wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwww.....',
  '.wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwk..',
  'wwbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbw.',
  '.wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwk..',
  '..ssssssssssss.................................ssssssssssss..',
];
const FERRY_KEY = { w: '#d8dce8', b: '#2a4a8a', y: '#ffe0a0', k: '#4a4e60', s: '#262a38' };
const ferryX = t => lerp(-70, 520, (t - FERRY.t0) / (FERRY.t1 - FERRY.t0));
function drawFerry(t) {
  if (t < FERRY.t0 || t >= FERRY.t1) return;
  const S = SPR.ferry, x = Math.round(ferryX(t)), y0 = FERRY.wl - S.height + 1 + Math.round(0.6 * wave(t, 80));
  halo(HL.ferry, x + 26, y0 + 4, 0.9);
  ctx.globalAlpha = 0.3; ctx.drawImage(S, 0, 2, S.width, 8, x, FERRY.wl + 1, S.width, 4); ctx.globalAlpha = 1;
  ctx.drawImage(S, x, y0);
  for (let k = 0; k < 6; k++) if (hash(k, step(t, 240), 5) < 0.6) R(x + 22 + k * 5, y0 + 4, 1, 2, '#3a3040');   // diners moving past the windows
  R(x + 2, y0 + 7, 1, 1, '#ff3a3a'); R(x + 57, y0 + 7, 1, 1, '#3aff7a'); R(x + 30, y0 - 3, 1, 3, '#8a8ea0'); R(x + 30, y0 - 4, 1, 1, '#ffffff');
  streak(ctx, t, x + 28, 22, '#ffd890', 0.25, 9, FERRY.wl + 2, EDGE);
  ctx.fillStyle = '#c8d4e8';
  for (let k = 1; k <= 10; k++) { ctx.globalAlpha = 0.45 * (1 - k / 11); ctx.fillRect(x - k * 3, FERRY.wl - (k & 1), 3, 1); }
  ctx.globalAlpha = 1;
}
// how much the ferry's wash laps the steps now (0..1): it reaches them a while after the ferry passes
const wash = t => env(t, 148, 164, 4);

/* ================= The Sky Tower's light show: colour rises up the shaft, cycles, fades back ================= */
const TINTS = ['#b070ff', '#ff5ab4', '#3ae0d0', '#5a8aff'];
function tintTower(src, col) {                                   // the white tower recoloured by luminance; warm windows kept
  const w = src.width, h = src.height, [c, x] = makeCanvas(w, h);
  x.drawImage(src, 0, 0);
  const img = x.getImageData(0, 0, w, h), d = img.data, T = hex(col);
  for (let i = 0; i < d.length; i += 4) {
    if (!d[i + 3]) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    if (r > b + 30) continue;
    const L = (0.3 * r + 0.59 * g + 0.11 * b) / 255, hi = Math.max(0, L - 0.78) * 2.2;
    for (let k = 0; k < 3; k++) d[i + k] = Math.round(clamp(T[k] * (0.2 + L * 0.95) + 255 * hi, 0, 255));
  }
  x.putImageData(img, 0, 0);
  return c;
}
function showState(t) {                                          // { fill row, [tint index, alpha] pairs } or null
  if (t < SHOW.r0 || t >= SHOW.t1) return null;
  const yF = Math.round(lerp(ST.foot, ST.tip - 1, smooth(SHOW.r0, SHOW.r1, t)));
  if (t < SHOW.c1 && t >= SHOW.r1) {
    const q = (t - SHOW.r1) / 3, i = Math.floor(q), f = smooth(0.7, 1, q - i);
    return { yF, tints: [[i % 4, 1 - f], [(i + 1) % 4, f]], chase: true };
  }
  if (t >= SHOW.c1) return { yF, tints: [[0, 1 - smooth(SHOW.c1, SHOW.t1, t)]] };
  return { yF, tints: [[0, 1]] };
}
function drawShow(t) {
  const st = showState(t);
  if (!st) return 0;
  let glow = 0;
  for (const [i, a] of st.tints) {
    if (a <= 0.01) continue;
    const T = TOWER_T[i], y0 = Math.max(0, st.yF - TOWER_BOX.y);
    ctx.globalAlpha = a; ctx.drawImage(T, 0, y0, T.width, T.height - y0, TOWER_BOX.x, TOWER_BOX.y + y0, T.width, T.height - y0); ctx.globalAlpha = 1;
    halo(HL.show[i], ST.cx, 74, a * smooth(ST.foot, 74, st.yF + 0.001) * 0.9);
    glow = Math.max(glow, a);
  }
  if (st.chase) { const y = Math.round(lerp(ST.mast, ST.tip + 1, frac(t / 1.5))); R(ST.cx, y, 1, 2, '#ffffff'); halo(HL.mastLight, ST.cx, y, 0.8); }
  return glow;
}
const showOn = t => t >= SHOW.r0 && t < SHOW.c1;

/* ================= The sky's and the water's small traffic ================= */
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(W + 10, -10, u)), y = Math.round(lerp(40, 58, u));
  R(x, y, 4, 1, '#5a5e70'); R(x + 2, y - 1, 1, 1, '#4a4e60'); R(x, y, 1, 1, '#f4f0e0');
  if (frac(t) < 0.18) R(x + 2, y + 1, 1, 1, '#ff4a3a');
  halo(HL.mastLight, x, y, 0.6);
}
function gull(x, y, flap, col = '#e8eaf0') {
  x = Math.round(x); y = Math.round(y);
  R(x, y, 1, 1, col); R(x + 4, y, 1, 1, col);
  if (flap) { R(x + 1, y - 1, 1, 1, col); R(x + 3, y - 1, 1, 1, col); } else { R(x + 1, y + 1, 1, 1, col); R(x + 3, y + 1, 1, 1, col); }
  R(x + 2, y, 1, 1, '#c8ccd8');
}
function drawGulls(t) {
  for (const [a, b, dir, y] of GULLS) {
    if (t < a || t >= b) continue;
    const u = (t - a) / (b - a);
    for (let k = 0; k < 3; k++) {
      const x = dir > 0 ? lerp(-20 - k * 14, W + 10 - k * 14, u) : lerp(W + 10 + k * 14, -20 + k * 14, u);
      gull(x, y + k * 5 + 2 * Math.sin(u * 9 + k), step(t + k * 0.13, 1200) % 2);
    }
  }
  if (t >= FERRY.t0 + 4 && t < FERRY.t1 - 6) for (let k = 0; k < 3; k++) {     // three follow the ferry, wheeling over it
    const fx = ferryX(t) + 30 + 18 * Math.cos(TAU * (t / 6 + k / 3)), fy = 150 + 8 * Math.sin(TAU * (t / 6 + k / 3)) - k * 4;
    gull(fx, fy, step(t + k * 0.21, 1440) % 2);
  }
}
// the red-billed gulls that sit on the steps' timber finger and the bollard, turning their heads
const PERCHED = [[STEPS.x0 - 5, STEPS.rows[0] - 8], [STEPS.x0 - 2, STEPS.rows[0] - 8], [341, EDGE - 8]];
function drawPerched(t) {
  PERCHED.forEach(([x, y], i) => {
    const look = hash(i, step(t, 160 + i * 16), 3) < 0.5 ? 1 : -1;
    R(x, y - 2, 3, 2, '#e8eaf0'); R(x, y - 1, 3, 1, '#c8ccd8'); R(x + (look > 0 ? 2 : 0), y - 3, 1, 1, '#f4f4f8');
    R(x + (look > 0 ? 3 : -1), y - 3, 1, 1, '#d8302a'); R(x, y - 2, 1, 1, '#4a4e5a'); R(x + 1, y, 1, 1, '#d8302a');
  });
}
function drawTaxi(t) {
  if (t < TAXI.t0 || t >= TAXI.t1) return;
  const x = Math.round(lerp(372, 140, (t - TAXI.t0) / (TAXI.t1 - TAXI.t0))), y = FAR_WL + 4;
  R(x, y - 3, 10, 2, '#d8b040'); R(x + 1, y - 1, 9, 1, '#8a7030'); R(x + 3, y - 5, 4, 2, '#e8e0d0'); R(x + 4, y - 4, 2, 1, '#ffe0a0');
  R(x, y - 3, 1, 1, '#ff3a3a'); halo(HL.anchor, x + 5, y - 4, 0.8);
  ctx.fillStyle = '#c8d4e8'; for (let k = 1; k <= 5; k++) { ctx.globalAlpha = 0.4 * (1 - k / 6); ctx.fillRect(x + 10 + k * 2, y - 1, 2, 1); } ctx.globalAlpha = 1;
}
function drawKayaks(t) {
  if (t < KAYAK.t0 || t >= KAYAK.t1) return;
  for (let k = 0; k < 2; k++) {
    const u = (t - KAYAK.t0 - k * 4) / (KAYAK.t1 - KAYAK.t0 - 8);
    if (u < 0 || u > 1) continue;
    const x = Math.round(lerp(370, -24, u)), y = KAYAK.wl + k, st = step(t + k * 0.7, 300) % 2;
    R(x, y - 1, 11, 2, k ? '#e0603a' : '#e8c040'); R(x + 1, y + 1, 9, 1, '#2a2a38');
    R(x + 4, y - 5, 3, 4, '#2a3a5a'); R(x + 4, y - 7, 3, 2, '#c09070'); R(x + 4, y - 7, 1, 1, '#ffffff'); halo(HL.anchor, x + 4, y - 7, 0.9);
    ctx.fillStyle = '#8a8070'; for (let j = -4; j <= 4; j++) ctx.fillRect(x + 5 + j, y - 4 + Math.round(j * (st ? 0.5 : -0.5)), 1, 1);
    R(x + 10, y - 2, 1, 1, '#ff3a3a');
  }
}
function drawFish(t) {
  for (const f of FISH) {
    if (t < f || t >= f + 3) continue;
    const u = (t - f) / 3, r = 1 + Math.round(u * 6);
    ctx.globalAlpha = 0.6 * (1 - u); ctx.fillStyle = '#a8b8e8';
    ctx.fillRect(196 - r, 180, r * 2 + 1, 1); ctx.fillRect(196 - r + 1, 179, 1, 1); ctx.fillRect(196 + r - 1, 181, 1, 1);
    ctx.globalAlpha = 1;
    if (u < 0.15) R(196, 178, 1, 2, '#c8d4f0');
  }
}
// the far promenade's walkers along the Viaduct's quay, tiny at this distance
const TINY = [{ n: 2, ph: 0.05, dir: 1, c: '#e8e2d4' }, { n: 2, ph: 0.3, dir: -1, c: '#c8404a' }, { n: 3, ph: 0.55, dir: 1, c: '#3a64c0' },
  { n: 2, ph: 0.8, dir: -1, c: '#e0b840' }, { n: 3, ph: 0.15, dir: -1, c: '#2a9a8a' }];
function drawTiny(t, crowd) {
  TINY.forEach((p, i) => {
    if (rk(i + 20) >= crowd * 1.6) return;
    const u = frac(p.n * t / LOOP + p.ph), x = Math.round(p.dir > 0 ? lerp(140, 372, u) : lerp(372, 140, u)), bob = step(t, 768) % 2;
    R(x, FAR_WL - 9 + bob, 1, 1, '#d8b898'); R(x, FAR_WL - 8 + bob, 1, 2, p.c); R(x + (bob ? 1 : 0), FAR_WL - 6, 1, 1, '#2a2226');
  });
}

/* ================= Scripted people: walkers and cyclists on the bridge, the bar's queue ================= */
// keys [t, x, y, alpha]: between two keys they walk if they move, otherwise they stand
function scriptAt(keys, t) {
  if (t < keys[0][0] || t >= keys[keys.length - 1][0]) return null;
  let k = 0; while (keys[k + 1][0] <= t) k++;
  const [t0, x0, y0, a0] = keys[k], [t1, x1, y1, a1] = keys[k + 1], u = (t - t0) / (t1 - t0);
  return { x: lerp(x0, x1, u), y: lerp(y0, y1, u), a: lerp(a0, a1, u), moving: x0 !== x1 || y0 !== y1, dir: x1 > x0 ? 1 : x1 < x0 ? -1 : 0 };
}
function route(t0, pts, speed, fadeIn = false, fadeOut = false) {
  const keys = [[t0, pts[0][0], pts[0][1], fadeIn ? 0 : 1]];
  let t = t0;
  for (let i = 1; i < pts.length; i++) {
    const [x, y, wait = 0, until] = pts[i], [px0, py0] = pts[i - 1];
    t += Math.max(0.6, Math.hypot(x - px0, y - py0) / speed);
    keys.push([t, x, y, 1]);
    if (until) { t = Math.max(t + 0.1, until); keys.push([t, x, y, 1]); } else if (wait) { t += wait; keys.push([t, x, y, 1]); }
  }
  if (fadeOut) keys[keys.length - 1][3] = 0;
  return keys;
}
// the bridge's people: a tiny figure (or a cyclist) on the deck; those caught by the lift wait at the barriers
const BE = 136, BW = 478, BAR_E = BR.PE - 14, BAR_W = BR.PW + 16;
const XINGS = [
  { keys: route(2, [[BE, 0], [BW, 0]], 13), col: '#e8e2d4' }, { keys: route(10, [[BW, 0], [BE, 0]], 12), col: '#c8404a' },
  { keys: route(30, [[BE, 0], [BW, 0]], 40), col: '#3a64c0', bike: true }, { keys: route(33, [[BE, 0], [BW, 0]], 40), col: '#e0b840', bike: true },
  { keys: route(44, [[BE, 0], [BAR_E, 0, 0, BRIDGE.clear], [BW, 0]], 13), col: '#2a9a8a' },
  { keys: route(46, [[BW, 0], [BAR_W, 0, 0, BRIDGE.clear + 0.6], [BE, 0]], 12), col: '#e0803a' },
  { keys: route(47, [[BW, 0], [BAR_W + 5, 0, 0, BRIDGE.clear + 1.2], [BE, 0]], 12), col: '#d87aa0' },
  { keys: route(70, [[BE, 0], [BAR_E - 6, 0, 0, BRIDGE.clear + 0.4], [BW, 0]], 36), col: '#c0c8d8', bike: true },
  { keys: route(128, [[BE, 0], [BW, 0]], 14), col: '#7a5ac0' }, { keys: route(150, [[BW, 0], [BE, 0]], 38), col: '#e8e2d4', bike: true },
  { keys: route(176, [[BE, 0], [BW, 0]], 12), col: '#5aa8d0' }, { keys: route(190, [[BW, 0], [BE, 0]], 14), col: '#4a8a4a' },
  { keys: route(212, [[BW, 0], [BE, 0]], 13), col: '#e0b840' }, { keys: route(222, [[BE, 0], [BW, 0]], 40), col: '#c8404a', bike: true },
];
function tinyPerson(x, fy, col, st, bike) {
  x = Math.round(x); fy = Math.round(fy);
  if (bike) {
    for (const wx of [x - 4, x + 3]) { R(wx, fy - 2, 3, 1, '#4a4c5c'); R(wx, fy, 3, 1, '#4a4c5c'); R(wx - 1, fy - 1, 1, 1, '#4a4c5c'); R(wx + 3, fy - 1, 1, 1, '#4a4c5c'); }
    R(x - 2, fy - 2, 5, 1, '#8a8c9c'); R(x - 1, fy - 6, 2, 4, col); R(x, fy - 8, 2, 2, '#c8a080');
    return;
  }
  R(x, fy - 8, 2, 2, '#c8a080'); R(x, fy - 6, 2, 3, col);
  if (st) { R(x - 1, fy - 3, 1, 3, '#2a2a38'); R(x + 2, fy - 3, 1, 3, '#2a2a38'); } else R(x, fy - 3, 2, 3, '#2a2a38');
}
function drawBridgeWalkers(t, crowd) {
  XINGS.forEach((w, i) => {
    if (crowd <= 0 || (rk(i + 40) >= crowd * 1.6 && i !== 4 && i !== 5)) return;
    const at = scriptAt(w.keys, t);
    if (!at) return;
    tinyPerson(at.x, deckY(at.x) - 1, w.col, at.moving && !w.bike ? step(t, 768) % 2 : 0, w.bike);
  });
}
// the bar's queue: a customer every 12 s, in from the right past the pavilion's end, served between the stools, then off
// along the front row with a coffee
const SERVE = 12, QX = PAV.x0 + 52;
const QKEYS = [[-14, 490, 210, 0], [-12.6, 472, 210, 1], [-9.5, QX, 210, 1], [0, QX, 210, 1], [1, QX - 4, 211, 1], [22.5, -14, 211, 1]];
const pLook = k => look(21 + (k % 3), k % 4, (k * 3 + 1) % 6, TOPS[(k * 5 + 2) % 12], BOTS[(k * 7) % 6], { long: k % 3 === 1 ? 3 : 0 });
const CUSTOMERS = Array.from({ length: 20 }, (_, k) => ({ k, S: SERVE * k + 6, P: pLook(k + 7), rank: k % 4 === 0 ? 0 : rk(k) }));
const qRel = (t, S) => ((t - S + 120) % LOOP + LOOP) % LOOP - 120;
let curCrowd = 1;
const serving = t => CUSTOMERS.some(cu => cu.rank < curCrowd && (r => r > -2.5 && r < 0)(qRel(t, cu.S)));
const steaming = t => CUSTOMERS.some(cu => cu.rank < curCrowd && (r => r > -7 && r < -3)(qRel(t, cu.S)));

// riders along the lower boardwalk (bikes and e-scooters), nearer than the front row
const RIDERS = [
  { n: 3, ph: 0.12, dir: 1, speed: 58, rank: rk(30), P: look(26, 1, 0, '#3a64c0', BOTS[1]), bike: true },
  { n: 2, ph: 0.62, dir: -1, speed: 46, rank: rk(31), P: look(25, 0, 3, '#e0b840', BOTS[0], { long: 3 }) },
  { n: 2, ph: 0.4, dir: -1, speed: 60, rank: rk(32), P: look(26, 2, 5, '#c8404a', BOTS[4]), bike: true },
];
const RIDE_Y = 240;
function riderAt(w, t) {
  const P = LOOP / w.n, s = ((t + w.ph * P) % P + P) % P, dur = (W + 60) / w.speed;
  if (s >= dur) return null;
  return { x: w.dir > 0 ? -30 + s * w.speed : W + 30 - s * w.speed, dir: w.dir };
}
function drawRider(w, at, t) {
  const x = Math.round(at.x), d = at.dir, y = RIDE_Y;
  if (w.bike) {
    for (const wx of [x - 7, x + 5]) { R(wx - 2, y - 5, 5, 1, '#3a3c4c'); R(wx - 2, y - 1, 5, 1, '#3a3c4c'); R(wx - 3, y - 4, 1, 3, '#3a3c4c'); R(wx + 3, y - 4, 1, 3, '#3a3c4c'); }
    R(x - 6, y - 4, 12, 1, '#8a8c9c'); R(x + d * 4, y - 9, 1, 5, '#8a8c9c');
    person(x - d, y - 7, w.P, { view: 2, dir: d, sit: true, aR: 3 });
    if (step(t, 768) % 2) R(x - 1, y - 6, 2, 3, w.P.bot); else R(x + 1, y - 5, 2, 3, w.P.bot);
  } else {
    R(x - 6, y - 1, 13, 1, '#2a2c38'); R(x - 6, y, 2, 1, '#14141c'); R(x + 5, y, 2, 1, '#14141c'); R(x + d * 6, y - 14, 1, 13, '#5a5c6c'); R(x + d * 6 - 1, y - 14, 3, 1, '#7a7c8c');
    person(x, y - 1, w.P, { view: 2, dir: d, aR: 3 });
  }
}

/* ================= Everything near, in feet order ================= */
function drawNear(t, crowd) {
  const DL = [], put = (y, f) => DL.push([y, f]), H1 = PAV.floor - 15;
  for (const c of CAST) if (c.rank < crowd) put(c.y, () => {
    person(c.x, c.y, c.P, castPose(c, t));
    if (c.id === 'stool1' || c.id === 'stool2') { R(c.x - 3, c.y + 1, 2, 6, c.P.bot); R(c.x + 1, c.y + 1, 2, 6, c.P.bot); R(c.x - 3, c.y + 6, 2, 1, c.P.shoe); R(c.x + 1, c.y + 6, 2, 1, c.P.shoe); }
    if (c.id === 'busk') {                                        // the ukulele across his lap, strummed
      const st = step(t, 960) % 2;
      R(c.x - 6, c.y - 6, 9, 3, '#c8884a'); R(c.x - 7, c.y - 5, 3, 3, '#a86a3a'); R(c.x + 3, c.y - 6, 5, 1, '#5a3a24'); R(c.x - 3, c.y - 5, 1, 1, '#2a1a10');
      R(c.x - 2 + st, c.y - 8, 2, 2, c.P.skin);
    }
    if (c.id === 'kid' && wash(t) < 0.3 && frac(t / 8) < 0.25) { ctx.globalAlpha = 0.6; R(c.x - 3, c.y + 2, 1, 1, '#c8d4f0'); R(c.x + 2, c.y + 1, 1, 1, '#c8d4f0'); ctx.globalAlpha = 1; }   // she kicks the water
    if (c.id === 'tabA') { R(TABLES[0].x - 5, TABLES[0].y - 2, 2, 2, '#e8c060'); R(TABLES[0].x + 4, TABLES[0].y - 2, 2, 2, '#e8e0d0'); }   // drinks on the table
  });
  // the counter in front of the staff (they stand inside, behind it), with what is on it
  put(197.5, () => {
    ctx.drawImage(PAV_C, PAV.x0 + 8, H1, PAV.x1 - PAV.x0 - 16, PAV.floor - H1, PAV.x0 + 8, H1, PAV.x1 - PAV.x0 - 16, PAV.floor - H1);
    R(PAV.x0 + 30, H1 - 2, 2, 2, '#e8e0d0'); R(PAV.x0 + 62, H1 - 3, 2, 3, '#e8c060');
    if (steaming(t)) for (let j = 1; j <= 6; j++) { ctx.globalAlpha = 0.5 - j * 0.07; R(PAV.x1 - 20 + Math.round(Math.sin(j * 1.3 + t * 3)), H1 - 10 - j, 1, 1, '#e8e8f0'); }
    ctx.globalAlpha = 1;
    if (serving(t)) R(QX - 4, H1 - 3, 2, 3, '#f4ecd8');                                   // the cup, handed over
  });
  put(LOUNGER.y - 1, () => { for (const L of LOUNGE) if (L.rank < crowd) recline(L.x, LOUNGER.y, L.P, L === LOUNGE[1] && frac(t / 30) < 0.6); });
  put(LOUNGER.y, () => ctx.drawImage(LOUNGE_C, 0, 0));
  for (const w of WALKERS) {
    if (w.rank >= crowd) continue;
    const at = walkerAt(w, t);
    if (!at) continue;
    put(at.y, () => {
      const st = step(t, w.jog ? 1536 : 768) % 2;
      person(at.x, at.y, w.P, { view: 2, dir: at.dir, step: st, aR: w.dog ? 3 : 0 });
      if (w.pair) person(at.x - at.dir * 7, at.y + 1, w.pair, { view: 2, dir: at.dir, step: 1 - st });
      if (w.dog) {
        const dx = Math.round(at.x + at.dir * 12), dy = Math.round(at.y);
        drawDog(dx, dy, at.dir, t);
        ctx.fillStyle = '#2a2026';
        for (let k = 1; k < 9; k++) ctx.fillRect(Math.round(lerp(at.x + at.dir * 4, dx - at.dir * 2, k / 9)), Math.round(lerp(at.y - 10, dy - 5, k / 9) + Math.sin(Math.PI * k / 9) * 2), 1, 1);
      }
    });
  }
  put(206, () => ctx.drawImage(HARA[sway(t)], HARA.x, HARA.y));                       // the harakeke in the breeze
  put(250, () => ctx.drawImage(GRASS[sway(t)], GRASS.x, GRASS.y));                     // the bottom planter's grasses
  for (const w of RIDERS) { if (w.rank >= crowd) continue; const at = riderAt(w, t); if (at) put(RIDE_Y, () => drawRider(w, at, t)); }
  for (const cu of CUSTOMERS) {
    if (cu.rank >= crowd) continue;
    const r = qRel(t, cu.S), at = scriptAt(QKEYS, r);
    if (!at) continue;
    put(at.y, () => {
      ctx.globalAlpha = at.a;
      person(at.x, at.y, cu.P, at.moving ? { view: 2, dir: at.dir || -1, step: step(t, 768) % 2, aR: r > 0 ? 5 : 0 } : { view: 0 });
      ctx.globalAlpha = 1;
    });
  }
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, f] of DL) f();
}

/* ================= Hidden things: a little blue penguin, a ruru on the light pole, a gecko on the container ================= */
const PENGUIN = { x: 118, y: 184, at: [[26, 50], [150, 172]] };   // it surfaces and paddles about by the steps
const RURU = { x: LAMP.x - 1, y: LAMP.y - 8 };                    // a morepork on the light pole's cap, out for the moths
const GECKO = { x: 364, y: 163 };                                 // a green gecko on the pavilion's end wall
function drawPenguin(t) {
  const w = PENGUIN.at.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const a = Math.min(1, (t - w[0]) / 2, (w[1] - t) / 2), x = PENGUIN.x + Math.round(2 * Math.sin(TAU * (t - w[0]) / 8)), y = PENGUIN.y;
  ctx.globalAlpha = a;
  R(x, y - 2, 5, 2, '#3a5a8a'); R(x, y - 1, 5, 1, '#d8dce8'); R(x + 4, y - 4, 2, 2, '#3a5a8a'); R(x + 6, y - 3, 1, 1, '#8a8a90'); R(x + 5, y - 4, 1, 1, '#e8e8f0');
  ctx.globalAlpha = 0.4 * a; R(x - 2, y, 9, 1, '#a8b8e8'); ctx.globalAlpha = 1;
}
function drawRuruGecko(t) {
  const blink = frac(t / 7.5) < 0.04;
  R(RURU.x, RURU.y, 4, 5, '#5a4030'); R(RURU.x, RURU.y, 4, 1, '#7a5a40'); R(RURU.x + 1, RURU.y + 3, 2, 2, '#8a6a4a');
  if (!blink) { R(RURU.x, RURU.y + 1, 1, 1, '#f0d040'); R(RURU.x + 3, RURU.y + 1, 1, 1, '#f0d040'); }
  R(RURU.x + 1, RURU.y + 5, 1, 1, '#c8a040'); R(RURU.x + 2, RURU.y + 5, 1, 1, '#c8a040');
  const g = GECKO, tl = step(t, 48) % 2;
  R(g.x + 1, g.y + 1, 4, 1, '#4ab04a'); R(g.x + 5, g.y, 1, 1, '#5ac05a'); R(g.x, g.y + 1 + tl, 1, 1, '#3a903a');
  R(g.x + 1, g.y, 1, 1, '#3a903a'); R(g.x + 4, g.y + 2, 1, 1, '#3a903a'); R(g.x + 1, g.y + 2, 1, 1, '#3a903a'); R(g.x + 4, g.y, 1, 1, '#3a903a');
}

/* ================= Small life: windows, lamps, anchor lights, glints ================= */
function drawWindows(t) {
  for (const w of WINS) R(w.x, w.y, 1, 1, hash(w.x, step(t + w.s * 12, w.k), w.y) < 0.55 ? w.on : w.off);
  for (const f of FLOORS) {
    if (hash(f.y, step(t + f.s * 30, f.k), f.x0) < 0.65) { ctx.fillStyle = f.on; for (let x = f.x0; x < f.x1; x += 4) ctx.fillRect(x, f.y, 3, 1); }
    else R(f.x0, f.y, f.x1 - f.x0, 1, f.off);
  }
}
// the breeze: a gust index (0, 1, 2) for the grasses, and the canopy's small sway
const gust = t => wave(t, 30) + 0.4 * wave(t, 71, 0.3);
const sway = t => { const g = gust(t); return g > 0.55 ? 2 : g < -0.55 ? 0 : 1; };
// the upstairs lounge: two people at the window, someone crossing the room twice a loop; the neon stutters now and then
const LOUNGE_WALK = [[30, 44], [150, 164]], NEON_FLICK = [[61.2, 62.4], [171.5, 172.3]];
function drawPavilionLife(t) {
  const x0 = PAV.x0, y0 = PAV.roof + 11;
  for (const [hx, ph] of [[x0 + 30, 0], [x0 + 64, 0.5]]) { const nod = frac(t / 16 + ph) < 0.2 ? 1 : 0; R(hx, y0 + 8 + nod, 3, 3, '#7a3e2a'); R(hx - 1, y0 + 11, 5, 4, '#8a4a30'); }
  const w = LOUNGE_WALK.find(([a, b]) => t >= a && t < b);
  if (w) { const cx = Math.round(lerp(x0 + 14, x0 + 82, (t - w[0]) / (w[1] - w[0]))); R(cx, y0 + 2, 3, 3, '#6a3424'); R(cx - 1, y0 + 5, 5, 9, '#6a3424'); }
  if (NEON_FLICK.some(([a, b]) => t >= a && t < b) && hash(Math.floor(t * 10), 7) < 0.5) { ctx.globalAlpha = 0.7; R(x0 + 86, PAV.roof + 11, 24, 12, '#4a1e16'); ctx.globalAlpha = 1; return false; }
  return true;
}
function drawTowerLamps(t) {
  const blink = frac(t / 1.5) < 0.5, glow = 0.75 + 0.25 * wave(t, 120);
  if (blink) { R(ST.cx, ST.tip + 1, 1, 1, '#ff4a40'); halo(HL.red, ST.cx, ST.tip + 1, 0.9); }
  for (const y of [24, 41]) { R(ST.cx, y, 1, 1, '#ff5a4a'); halo(HL.red, ST.cx, y, 0.55 * glow); }
  if (frac(t / 2) < 0.5) for (const [x, y] of AIR_LAMPS) { R(x, y, 1, 1, '#ff4a3a'); halo(HL.red, x, y, 0.45); }
}
function drawGlints(t) {
  const fr = Math.floor(t * 60);
  ctx.fillStyle = '#e8e8ff';
  for (let k = 0; k < 30; k++) {                                // the moon's broken path on the basin and the channel
    const h1 = hash(k, fr, 7);
    if (h1 < 0.5) continue;
    const y = FAR_WL + 2 + k * 1.2, x = MOON.x + Math.round((hash(k, fr, 8) - 0.5) * (4 + k * 0.5));
    ctx.globalAlpha = 0.3 + 0.3 * h1; ctx.fillRect(x, Math.round(y), 1 + (h1 > 0.85 ? 1 : 0), 1);
  }
  ctx.fillStyle = '#ffd890';
  for (let k = 0; k < 90; k++) {                                // warm sparkle under the Viaduct's lamps and bars
    const q = PROM_LAMPS[k % PROM_LAMPS.length], h1 = hash(k, fr, 9);
    if (h1 < 0.5) continue;
    ctx.globalAlpha = 0.2 + 0.3 * h1;
    ctx.fillRect(q + Math.round((hash(k, fr, 10) - 0.5) * 12), FAR_WL + 1 + Math.floor(hash(k, fr, 11) * 12), 2, 1);
  }
  for (let k = 0; k < 90; k++) {                                // the channel lapping in front: the masts' blue, the bar's gold
    const h1 = hash(k, fr, 15);
    if (h1 < 0.4) continue;
    const x = Math.floor(hash(k, fr, 16) * PAV.x0), y = 182 + Math.floor(hash(k, fr, 17) * (EDGE - 183));
    ctx.fillStyle = x > 330 ? '#ffc880' : Math.abs(x - BR.PE) < 30 || Math.abs(x - BR.PW) < 30 ? '#8aa4ff' : '#a8b4e8';
    ctx.globalAlpha = 0.2 + 0.3 * h1; ctx.fillRect(x, y, 2, 1);
  }
  ctx.fillStyle = '#8a9ad8';
  for (let k = 0; k < 140; k++) {                               // cool glints on the open water
    const h1 = hash(k, fr, 12);
    if (h1 < 0.5) continue;
    ctx.globalAlpha = 0.18 + 0.22 * h1;
    ctx.fillRect(Math.floor(hash(k, fr, 13) * W), FAR_WL + 3 + Math.floor(hash(k, fr, 14) * (EDGE - FAR_WL - 5)), 2, 1);
  }
  ctx.globalAlpha = 1;
}
// the festoon: bulbs hung on a sagging wire from the pavilion's top corner to the timber pole
const FESTOON = [];
for (const [ay, by, sag] of [[PAV.roof - 7, POLE.top, 10], [PAV.mid + 4, POLE.top + 14, 6]]) {
  const ax = PAV.x0, bx = POLE.x + 1;
  for (let k = 0; k <= 34; k++) { const u = k / 34; FESTOON.push({ x: Math.round(lerp(ax, bx, u)), y: Math.round(lerp(ay, by, u) + Math.sin(Math.PI * u) * sag), bulb: k % 2 === 1 }); }
}
function drawFestoon(t) {
  for (const f of FESTOON) {
    if (!f.bulb) { R(f.x, f.y, 1, 1, '#2a2026'); continue; }
    const on = 0.8 + 0.2 * hash(f.x, step(t, 1200));
    R(f.x, f.y, 1, 1, '#2a2026'); R(f.x, f.y + 1, 1, 1, on > 0.85 ? '#fff0c0' : '#ffd890');
    halo(HL.bulb, f.x, f.y + 1, on);
  }
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 7, 3, 1, '#f2dcaa'); halo(HL.bbLamp, lx, BB.y - 6, 0.9); }
}
function mastApex(side, th) {
  const i = side < 0 ? 0 : 1, bx = side < 0 ? BR.PE : BR.PW, by = deckY(bx) - 2, a = MAST_LEAN + th * 0.55;
  return [Math.round(bx + side * Math.sin(a) * MAST_H[i]), Math.round(by - Math.cos(a) * MAST_H[i])];
}
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
function trim(c) {                                // crop a full-frame layer to its painted box
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) { if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
let HL, SPR, SKY_C, MOON_C, WISP_C, CITY_C, TOWER_C, TOWER_BOX, TOWER_T, VIADUCT_C, BB_C, BB_AD, PITCH, WATER_C, MOORED_FAR, MOORED_NEAR,
  TEWERO_C, BRIDGE_C, LEAVES_SHUT, PLAZA_C, PAV_C, BRANCH_C, VIG_C, RIPPLES, LOUNGE_C, TRUNK_C, HARA, GRASS;
function prerender() {
  HL = {
    moon: makeHalo(40, '#9aa8ff', 0.2, 1.8), moonCore: makeHalo(14, '#f2f0ff', 0.3, 1.5),
    haze: makeHalo(170, '#8a6aff', 0.06, 1.3), pod: makeHalo(38, '#b8b0ff', 0.16, 1.6), podCore: makeHalo(14, '#f4f0ff', 0.24, 1.4),
    shaft: makeHalo(24, '#a8a0ec', 0.1, 1.5), red: makeHalo(4, '#ff3a30', 0.6, 1), blue: makeHalo(9, '#5a7cff', 0.4, 1.3),
    mastLight: makeHalo(4, '#ffffff', 0.4, 1.2), anchor: makeHalo(3, '#fff4e0', 0.35, 1.2), amber: makeHalo(7, '#ffa030', 0.6, 1.2),
    bar: makeHalo(64, '#ffaa58', 0.24, 1.5), glassEnd: makeHalo(22, '#ffc070', 0.22, 1.5), bulb: makeHalo(5, '#ffd080', 0.3, 1.3),
    lamp: makeHalo(48, '#ffb866', 0.2, 1.7), lampCore: makeHalo(12, '#ffe6b0', 0.45, 1.4), prom: makeHalo(5, '#ffd890', 0.35, 1.3),
    cabin: makeHalo(12, '#c8d8ff', 0.22, 1.4), lounge: makeHalo(40, '#ffb870', 0.12, 1.5), neon: makeHalo(16, '#ff7a90', 0.28, 1.4),
    candle: makeHalo(12, '#ffb860', 0.3, 1.5), ferry: makeHalo(34, '#ffd890', 0.18, 1.5),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(34, '#ffb870', 0.06, 1.5),
    show: TINTS.map(c => makeHalo(44, c, 0.22, 1.6)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  { const sx = SKY_C.getContext('2d');                           // the haze and the moon's glow never change: bake them
    drawHalo(sx, HL.haze, 170, 160, 1); drawHalo(sx, HL.moon, MOON.x, MOON.y); drawHalo(sx, HL.moonCore, MOON.x, MOON.y); }
  WINS.length = 0; BLOOMS.length = 0; FLOORS.length = 0;
  CITY_C = renderCity();
  for (const f of FLOORS) f.off = mixHex(f.b.mat[1], HAZE, f.b.dk);
  { const tw = trim(renderTower()); TOWER_C = tw.c; TOWER_BOX = { x: tw.x, y: tw.y }; TOWER_T = TINTS.map(c => tintTower(tw.c, c)); }
  VIADUCT_C = renderViaduct();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#5e60ce' });
  WATER_C = renderWater(); RIPPLES = [renderRipples(5), renderRipples(6)]; MOORED_FAR = renderMoored(true); MOORED_NEAR = renderMoored(false); TEWERO_C = renderTeWero();
  BRIDGE_C = renderBridgeFixed();
  { const [c, x] = makeCanvas(W, H); drawMast(x, -1, 0); drawMast(x, 1, 0); drawLeaf(x, -1, 0); drawLeaf(x, 1, 0); LEAVES_SHUT = c; }
  PLAZA_C = renderPlaza(); LOUNGE_C = renderLounger(); PAV_C = renderPavilion(); BRANCH_C = trim(renderBranch()); TRUNK_C = renderTrunk(); VIG_C = renderVignette();
  const frames = part => { const fr = [-1, 0, 1].map(sw => trim(renderGrass(sw, part))), x = Math.min(...fr.map(f => f.x)), y = Math.min(...fr.map(f => f.y));
    const out = fr.map(f => { const [c, cx] = makeCanvas(W - x, H - y); cx.drawImage(f.c, f.x - x, f.y - y); return c; }); out.x = x; out.y = y; return out; };
  HARA = frames(0); GRASS = frames(1);
  SPR = { ferry: sprite(FERRY_ROWS, FERRY_KEY) };
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  curCrowd = crowd;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // the sky: stars, the moon, wisps of sea cloud, a plane
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  if (wx.blossoms) drawStamens(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the city centre and the Sky Tower (its rings brighten before the show)
  const pre = env(t, SHOW.t0, SHOW.r0 + 4, 3);
  halo(HL.pod, ST.cx, 72, 0.9 + 0.4 * pre); halo(HL.shaft, ST.cx, 112, 0.8); halo(HL.shaft, ST.cx, 36, 0.5);
  ctx.drawImage(CITY_C, 0, 0);
  drawWindows(t);
  ctx.drawImage(TOWER_C, TOWER_BOX.x, TOWER_BOX.y);
  const glow = drawShow(t);
  halo(HL.podCore, ST.cx, 76, 0.75 + 0.1 * wave(t, 12) + 0.6 * pre);
  if (pre > 0.05) for (const y of [73, 59]) { ctx.globalAlpha = pre * 0.8; R(ST.cx - (y === 73 ? 11 : 6), y, y === 73 ? 23 : 13, 1, '#ffffff'); ctx.globalAlpha = 1; }
  drawTowerLamps(t);
  if (billboard) drawBillboard(t, pitch);
  // the Viaduct: apartments, bars, its promenade's walkers, the yachts at the far quay
  ctx.drawImage(VIADUCT_C, 0, 0);
  drawTiny(t, crowd);
  ctx.drawImage(MOORED_FAR, 0, 0);
  for (const x of PROM_LAMPS) halo(HL.prom, x, FAR_WL - 7, 0.9);
  // the basin: its mirror, ripples, streaks and glints, the water taxi
  ctx.drawImage(WATER_C, 0, FAR_WL);
  reflect(ctx, t, { top: FAR_WL, rows: EDGE - FAR_WL, squash: 1.1, alpha: 0.3, amp: 1, grow: 0.07, k1: 120, k2: 960 });
  ctx.globalAlpha = 0.32; drawScroll(RIPPLES[0], FAR_WL, 12, t); ctx.globalAlpha = 0.24; drawScroll(RIPPLES[1], FAR_WL, -8, t); ctx.globalAlpha = 1;
  PROM_LAMPS.forEach((x, i) => streak(ctx, t, x, 2 + (i % 2), '#ffc878', 0.45, x * 0.7, FAR_WL + 1, 182));
  const sh = showState(t);
  streak(ctx, t, ST.cx, 5, sh ? TINTS[sh.tints[sh.tints.length - 1][0]] : '#d8d0ff', 0.16 + 0.2 * glow, 3, FAR_WL + 2, EDGE);
  drawGlints(t);
  drawTaxi(t);
  // nearer on the water: Te Wero Island and its yachts, with their mirror
  ctx.drawImage(TEWERO_C, 0, 0);
  ctx.drawImage(MOORED_NEAR, 0, 0);
  reflect(ctx, t, { top: 174, rows: EDGE - 174, x: 50, w: 104, squash: 1.2, alpha: 0.3, k1: 120, k2: 720 });
  MASTHEADS.forEach((m, i) => { const a = 0.6 + 0.4 * hash(i, step(t, 480), 3); R(m.x, m.y, 1, 1, '#fff6e0'); halo(HL.anchor, m.x, m.y, a); });
  // the bridge, its people, the yacht coming through when it is open
  const Y = yachtAt(t);
  if (Y && Y.y < 172) drawEventYacht(t, Y);
  ctx.drawImage(BRIDGE_C, 0, 0);
  const th = leafAngle(t);
  if (th > 0.001) { drawMast(ctx, -1, th); drawMast(ctx, 1, th); drawLeaf(ctx, -1, th); drawLeaf(ctx, 1, th); }
  else ctx.drawImage(LEAVES_SHUT, 0, 0);
  for (const side of [-1, 1]) { const [ax, ay] = mastApex(side, th); halo(HL.blue, ax, ay, 0.8 + 0.2 * wave(t, 60, side * 0.25)); }
  if (lifting(t) && frac(t / 1.2) < 0.5) for (const x of [BAR_E + 6, BR.PE + 8, BR.PW - 8, BAR_W - 6]) { R(x, deckY(x) - 6, 1, 1, '#ffb040'); halo(HL.amber, x, deckY(x) - 6, 0.9); }
  halo(HL.cabin, BR.PW + 14, deckY(BR.PW) - 6, 0.8);
  drawBridgeWalkers(t, crowd);
  reflect(ctx, t, { top: 184, rows: EDGE - 184, x: BR.x0, w: W - BR.x0, squash: 1.4, alpha: 0.28, amp: 0.9, k1: 120, k2: 960 });
  streak(ctx, t, BR.PE - 3, 3, '#7a96ff', 0.35, 11, 182, EDGE);
  streak(ctx, t, BR.PW + 4, 3, '#7a96ff', 0.35, 13, 184, EDGE);
  streak(ctx, t, 352, 5, '#ffb868', 0.3, 17, 184, EDGE);
  if (Y && Y.y >= 172) drawEventYacht(t, Y);
  drawKayaks(t);
  drawFerry(t);
  drawFish(t);
  drawPenguin(t);
  const ws = wash(t);
  if (ws > 0) { ctx.fillStyle = '#c8d4f0'; for (let k = 0; k < 40; k++) { const h1 = hash(k, Math.floor(t * 20), 31); if (h1 > ws) continue; ctx.globalAlpha = 0.5 * ws; ctx.fillRect(STEPS.x0 + Math.floor(hash(k, 32) * (STEPS.x1 - STEPS.x0)), STEPS.rows[0] + Math.floor(h1 * 5), 3, 1); } ctx.globalAlpha = 1; }
  drawGulls(t);
  if (wx.blossoms) drawStamens(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // Karanga Plaza: the steps, the deck, the pavilion bar, its people, the festoon and the lamps
  ctx.drawImage(PLAZA_C, 0, 0);
  ctx.drawImage(PAV_C, 0, 0);
  halo(HL.glassEnd, PAV.x0 + 2, PAV.roof + 22, 0.85); halo(HL.lounge, PAV.x0 + 48, PAV.roof + 20, 0.8);
  if (drawPavilionLife(t)) halo(HL.neon, PAV.x0 + 98, PAV.roof + 16, 0.75 + 0.1 * wave(t, 48));
  drawPerched(t);
  drawNear(t, crowd);
  halo(HL.bar, PAV.x0 + 56, 182, 0.95 + 0.05 * wave(t, 40));
  for (const bx of [PAV.x0 + 42, PAV.x0 + 70]) halo(HL.bulb, bx, PAV.mid + 10, 1);
  for (const T of TABLES) { const f = 0.75 + 0.25 * hash(T.x, step(t, 1440)); R(T.x, T.y - 3, 1, 1, f > 0.85 ? '#fff4c0' : '#ffc860'); halo(HL.candle, T.x, T.y - 3, f); }
  drawFestoon(t);
  halo(HL.lamp, LAMP.x, LAMP.y + 2, 1); halo(HL.lampCore, LAMP.x, LAMP.y, 0.9);
  // the pohutukawa overhead, its stamens falling past, the picked weather's near depth
  ctx.drawImage(TRUNK_C, 0, 0);
  ctx.drawImage(BRANCH_C.c, BRANCH_C.x + Math.round(0.9 * gust(t) / 1.4), BRANCH_C.y + Math.round(0.6 * wave(t, 40, 0.3)));
  drawRuruGecko(t);
  if (wx.blossoms) drawStamens(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'auckland',
  name: 'Auckland',
  tagline: 'Sky Tower over the Viaduct',
  timeZone: 'Pacific/Auckland',
  accent: '#5e60ce',
  focusX: ST.cx,
  landmark: [ST.cx - 12, ST.cx + 12],                           // the Sky Tower's pod, its widest part
  landmarkTop: ST.tip,                                           // the mast's tip
  billboard: BB,
  crowd: CROWD,
  spots: [                                                       // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: ST.cx - 12, y: ST.tip, w: 25, h: ST.seen - ST.tip },                     // the Sky Tower
    { kind: 'water', x: 150, y: 171, w: 90, h: 8 },                                                // the channel under the bridge
    { kind: 'window', x: PAV.x0 + 12, y: PAV.roof + 10, w: 54, h: 26 },                             // the lounge upstairs in the pavilion
    { kind: 'birds', x: STEPS.x0 - 6, y: STEPS.rows[0] - 12, w: 8, h: 5 },                           // the gulls on the steps' timber post
  ],
  ambience: { water: 0.45, waves: 0.15, crowd: 0.25, wind: 0.14, traffic: 0.08 },
  events: [
    { t: 10, type: 'gull', x: 400 }, { t: 18, type: 'boat', dur: 28, pan: [0.4, -0.3], gain: 0.22 },
    { t: 44, type: 'splash', x: 196, gain: 0.15 },
    { t: BRIDGE.warn, type: 'bell', x: BR.PE, gain: 0.4 }, { t: BRIDGE.warn + 1.6, type: 'bell', x: BR.PW, gain: 0.35 },
    { t: YACHT.t0, type: 'boat', dur: YACHT.t1 - YACHT.t0, pan: [0.15, -1], gain: 0.4 }, { t: BRIDGE.down0, type: 'bell', x: ST.cx, gain: 0.3 },
    { t: 112, type: 'gull', x: 80 }, { t: 126, type: 'horn', x: 40 }, { t: FERRY.t0, type: 'boat', dur: FERRY.t1 - FERRY.t0, pan: [-1, 0.8], gain: 0.45 },
    { t: 140, type: 'gull', x: 240 }, { t: 140, type: 'splash', x: 196, gain: 0.15 }, { t: 152, type: 'splash', x: 200, gain: 0.3 },
    { t: 158, type: 'horn', x: 420, gain: 0.6 },
    { t: SHOW.r0, type: 'chime', x: ST.cx, gain: 0.3 }, { t: SHOW.r1, type: 'chime', x: ST.cx, gain: 0.3 },
    { t: 204, type: 'splash', x: 196, gain: 0.15 }, { t: 218, type: 'gull', x: 380 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
